import React, { useState, useMemo, useRef, useEffect, useSyncExternalStore } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown, Trophy, Play, Grid3X3, PenLine, Target, X, Undo2, Pencil, Check, Trash2, Clock, AlertTriangle } from 'lucide-react'
import { useGames, useGame, useUpsertScore, useDeleteScore, useUpdateGame, usePresses, useCreatePress, useDeletePress, useCreateSettlements, useDeleteGame, useResumeGame, useUpdateScoreTaken } from '../hooks/useGames'
import { useAuth } from '../contexts/AuthContext'
import { useMatch, useUpdateMatch, useDeleteMatch, useActivateSession, useDeactivateSession, useDeleteSession } from '../hooks/useMatches'
import { useRealtimeScores } from '../hooks/useRealtimeScores'
import { useSideBets, useCtpResults, useUpsertCtpResult, useDeleteCtpResult } from '../hooks/useSideBets'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { netScore } from '../utils/handicaps'
import { getGameType } from '../constants/gameTypes'
import { computeRoundResults, computeMoneyResults, stablefordPoints, getPressTriggers, getPressLabel } from '../utils/scoringEngine'
import { computeSkinsMoney, computeCtpMoney, computeLowNetMoney } from '../utils/sideBetEngine'
import { detectScoreEvents, detectMatchEvents, detectClinchEvents, detectDormieEvents, buildScoreSummary } from '../utils/tickerEngine'
import { useEmitTickerEvents } from '../hooks/useTicker'
import { getInitials } from '../utils/colors'
import { buildRoundSnapshot } from '../utils/resultSnapshot'
import { buildGameHandicapBreakdown, buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { fetchGame as fetchGameApi, createSettlements as createSettlementsApi, updateGame as updateGameApi } from '../api/games'
import { fetchSideBets as fetchSideBetsApi } from '../api/sideBets'
import { useGameScoring, computePressHoleStatuses } from '../hooks/useGameScoring'
import { getScore as getScorePure, getStrokesOnHole as getStrokesOnHolePure, getTotal as getTotalPure, getNetTotal as getNetTotalPure, getScoreTaken as getScoreTakenPure, formatMatchDiff, formatPointDiff, teamAResultText } from '../utils/scorecardHelpers'
import { mergeScoresWithQueue, getCachedGameData, getQueueStats } from '../utils/scoreQueue'
import logger from '../utils/logger'
import { onlineManager } from '@tanstack/react-query'
import ScoreIndicator from './ScoreIndicator'
import ScorePopover from './ScorePopover'
import GameTypeBadge from './GameTypeBadge'
import RoundSettingsEditor from './RoundSettingsEditor'
import { RoundItem } from './MatchDashboard'

// Hook that re-renders when the score queue changes (localStorage-backed)
let queueVersion = 0
function subscribeToQueue(callback) {
  const handler = () => { queueVersion++; callback() }
  window.addEventListener('score-queue-changed', handler)
  return () => window.removeEventListener('score-queue-changed', handler)
}
function getQueueSnapshot() { return queueVersion }

function useQueueVersion() {
  return useSyncExternalStore(subscribeToQueue, getQueueSnapshot)
}

export default function ActiveGame({ matchId, onViewGame, currentHole, onHoleChange, onBackToMatch, onBackToHome, viewedSessionId, onViewSession, viewedGameId, onViewGameId, endRoundRequested, onEndRoundHandled }) {
  const { user } = useAuth()
  const { data: games, isLoading: gamesLoading, isFetching: gamesFetching, refetch: refetchGames } = useGames()
  const { data: match, isLoading: matchLoading, isFetching: matchFetching, refetch: refetchMatch } = useMatch(matchId)
  const resumeGame = useResumeGame()
  const activateSessionMutation = useActivateSession()
  const [showResumeConfirm, setShowResumeConfirm] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    try { return sessionStorage.getItem(`matchup_${matchId}`) || null } catch { return null }
  })
  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId)
    try { sessionStorage.setItem(`matchup_${matchId}`, groupId) } catch {}
  }

  const isTeamMatch = match?.match_type === 'team_match' || match?.match_type === 'invitational'

  // For team matches: find all active sessions and their groups with linked games
  const activeSessions = isTeamMatch
    ? (match?.sessions || []).filter(s => s.status === 'in_progress')
    : []

  // Require a specific session to be selected — show only that session's matchups
  const viewedActiveSession = isTeamMatch && viewedSessionId
    ? activeSessions.find(s => s.id === viewedSessionId) || null
    : activeSessions.length === 1 ? activeSessions[0] : null
  const activeSession = viewedActiveSession

  const sessionGroups = useMemo(() => {
    if (!viewedActiveSession) return []
    return (viewedActiveSession.session_groups || [])
      .slice()
      .sort((a, b) => a.group_order - b.group_order)
  }, [viewedActiveSession])

  // Find all in_progress games for this match (from standalone games query + match.games fallback)
  const activeGames = useMemo(() => {
    if (!matchId) return []
    const standalone = games ? games.filter(g => g.match_id === matchId && g.status === 'in_progress') : []
    if (standalone.length > 0) return standalone
    // Fallback: match.games may have fresher data after session activation
    return (match?.games || []).filter(g => g.status === 'in_progress')
  }, [games, match, matchId])

  // For team matches with session groups, match games to groups
  const groupGameMap = useMemo(() => {
    if (sessionGroups.length === 0 || activeGames.length === 0) return {}
    const map = {}
    for (const group of sessionGroups) {
      // Match via session_group_id on the game, or game_id on the group
      const game = activeGames.find(g => g.session_group_id === group.id)
        || (group.game_id ? activeGames.find(g => g.id === group.game_id) : null)
      if (game) map[group.id] = game
    }
    return map
  }, [sessionGroups, activeGames])

  // Default selectedGroupId to first group that has a game
  const effectiveGroupId = selectedGroupId && groupGameMap[selectedGroupId]
    ? selectedGroupId
    : sessionGroups.find(g => groupGameMap[g.id])?.id || null

  // For team matches, render with matchup selector when games are mapped
  if (isTeamMatch && sessionGroups.length > 0 && Object.keys(groupGameMap).length > 0) {
    const selectedGame = effectiveGroupId ? groupGameMap[effectiveGroupId] : null
    if (selectedGame) {
      return (
        <Scorecard
          game={selectedGame}
          matchId={matchId}
          onViewGame={onViewGame}
          currentHole={currentHole}
          onHoleChange={onHoleChange}
          onBackToMatch={onBackToMatch}
          onBackToHome={onBackToHome}
          sessionGroups={sessionGroups}
          groupGameMap={groupGameMap}
          selectedGroupId={effectiveGroupId}
          onSelectGroup={handleSelectGroup}
          endRoundRequested={endRoundRequested}
          onEndRoundHandled={onEndRoundHandled}
        />
      )
    }
  }

  // Active session exists but games haven't loaded yet — show loading or retry
  if (isTeamMatch && activeSession && sessionGroups.length > 0 && Object.keys(groupGameMap).length === 0) {
    const isLoading = gamesLoading || matchLoading || gamesFetching || matchFetching || activateSessionMutation.isPending
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '0.5rem',
      }}>
        <span style={{ fontSize: '1.5rem' }}>⛳</span>
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.125rem' }}>
            {isLoading ? 'Loading session...' : 'Waiting for session games...'}
          </h2>
          {!isLoading && (
            <button
              onClick={() => { refetchGames(); refetchMatch() }}
              style={{
                marginTop: '0.5rem',
                padding: '0.375rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: 'var(--accent-primary)',
                backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.3)',
                borderRadius: '9999px',
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          )}
        </div>
      </div>
    )
  }

  // Find active game: prefer one in this match, fallback to any active game
  // Skip for team matches with a specific session selected — let the drill-down handle it
  const activeGame = matchId
    ? games?.find((g) => g.match_id === matchId && g.status === 'in_progress')
    : games?.find((g) => g.status === 'in_progress')

  if (activeGame && !(isTeamMatch && viewedSessionId)) {
    return <Scorecard game={activeGame} matchId={matchId} onViewGame={onViewGame} currentHole={currentHole} onHoleChange={onHoleChange} onBackToMatch={onBackToMatch} onBackToHome={onBackToHome} endRoundRequested={endRoundRequested} onEndRoundHandled={onEndRoundHandled} />
  }

  // While data is still loading, fall back to the last-known active game id
  // from localStorage rather than blanking the screen. Scorecard's useGame()
  // call hydrates from the per-game cache (cacheGameData) so the user sees
  // their scorecard immediately on resume even if useGames() hasn't populated.
  // Only render the cached path when we have real per-game cache data behind
  // the id — passing a bare shim with no full game data would render a broken
  // scorecard.
  if (gamesLoading || matchLoading) {
    let cachedGameId = null
    try { cachedGameId = matchId ? localStorage.getItem(`press_active_game_${matchId}`) : null } catch { /* ignore */ }
    if (cachedGameId && getCachedGameData(cachedGameId)) {
      logger.info('ActiveGame: rendering Scorecard from cached activeGameId', { matchId, cachedGameId })
      const shimGame = { id: cachedGameId, match_id: matchId }
      return <Scorecard game={shimGame} matchId={matchId} onViewGame={onViewGame} currentHole={currentHole} onHoleChange={onHoleChange} onBackToMatch={onBackToMatch} onBackToHome={onBackToHome} endRoundRequested={endRoundRequested} onEndRoundHandled={onEndRoundHandled} />
    }
    logger.info('ActiveGame: gated by gamesLoading||matchLoading with no cached gameId', { matchId, gamesLoading, matchLoading })
    return null
  }

  // Use match.games (has full nested data: scores, teams, etc.) for completed rounds
  // For session-based matches, exclude session games — they're managed on the EVENT tab
  const completedGames = (match?.games || []).filter((g) =>
    g.status === 'completed' && !(isTeamMatch && g.session_group_id)
  )
  const matchPlayers = match?.match_players || []
  const isOwner = match?.owner_id === user?.id

  // "Enter round" for non-team matches: render Scorecard for the viewed game
  if (!isTeamMatch && viewedGameId) {
    const viewedGame = (match?.games || []).find(g => g.id === viewedGameId)
    if (viewedGame) {
      return (
        <Scorecard
          game={viewedGame}
          matchId={matchId}
          onViewGame={onViewGame}
          currentHole={currentHole}
          onHoleChange={onHoleChange}
          onBackToMatch={() => onViewGameId?.(null)}
          onBackToHome={onBackToHome}
        />
      )
    }
    // Game not found — clear and fall through
    onViewGameId?.(null)
  }

  // Drill-down into a specific session (completed or in-progress): render Scorecard with matchup selector
  if (isTeamMatch && viewedSessionId && !viewedActiveSession) {
    const allGames = match?.games || []
    const allSessions = (match?.sessions || [])
      .sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0))
    const session = allSessions.find(s => s.id === viewedSessionId)
    if (session) {
      const sessionGroupsSorted = (session.session_groups || []).slice().sort((a, b) => a.group_order - b.group_order)
      const completedGroupGameMap = {}
      for (const group of sessionGroupsSorted) {
        const game = allGames.find(g => g.session_group_id === group.id)
          || (group.game_id ? allGames.find(g => g.id === group.game_id) : null)
        if (game) completedGroupGameMap[group.id] = game
      }
      const effectiveCompletedGroupId = selectedGroupId && completedGroupGameMap[selectedGroupId]
        ? selectedGroupId
        : sessionGroupsSorted.find(g => completedGroupGameMap[g.id])?.id || null
      const selectedCompletedGame = effectiveCompletedGroupId ? completedGroupGameMap[effectiveCompletedGroupId] : null

      if (selectedCompletedGame) {
        return (
          <Scorecard
            game={selectedCompletedGame}
            matchId={matchId}
            onViewGame={onViewGame}
            currentHole={currentHole}
            onHoleChange={onHoleChange}
            onBackToMatch={() => onViewSession(null)}
            onBackToHome={onBackToHome}
            sessionGroups={sessionGroupsSorted}
            groupGameMap={completedGroupGameMap}
            selectedGroupId={effectiveCompletedGroupId}
            onSelectGroup={handleSelectGroup}
          />
        )
      }
    }
    // Session not found or no games — reset and fall through
    onViewSession(null)
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '0.5rem',
      }}>
        <span style={{ fontSize: '1.5rem' }}>⛳</span>
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.125rem' }}>
            {isTeamMatch ? 'No Active Session' : 'No Active Round'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
            {isTeamMatch
              ? 'Activate a session from the Event tab'
              : `Start a new round from the ${match?.match_type === 'trip' ? 'Trip' : match?.match_type === 'quick_round' ? 'Round' : 'Match'} tab`}
          </p>
        </div>
      </div>

      {/* Show completed sessions as clickable session cards */}
      {isTeamMatch && (() => {
        const completedSessions = (match?.sessions || [])
          .filter(s => s.status === 'completed')
          .sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0))
        if (completedSessions.length === 0) return null
        const allGames = match?.games || []
        const matchTeams = match?.match_teams || []

        // Session card list (mirrors EVENT tab style)
        return (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {completedSessions.map(session => {
                const groups = (session.session_groups || []).sort((a, b) => (a.group_order ?? 0) - (b.group_order ?? 0))
                return (
                  <div
                    key={session.id}
                    onClick={() => onViewSession(session.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border-color)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ padding: '0.75rem', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {/* Top row: status badge + chevron */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{
                          fontSize: '0.625rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '9999px',
                          color: 'var(--text-muted)',
                          backgroundColor: 'var(--bg-secondary)',
                        }}>
                          Completed
                        </span>
                        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                      </div>

                      {/* Middle: icon + session name + game type badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                          flexShrink: 0,
                        }}>
                          <Check size={16} style={{ color: 'var(--accent-primary)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                              {session.session_name || 'Session'}
                            </span>
                            {session.game_type && (
                              <GameTypeBadge gameTypeId={session.game_type} size="small" />
                            )}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
                            {session.course_name || ''}{session.tee_name ? ` — ${session.tee_name}` : ''}
                            {session.num_holes ? ` · ${session.num_holes} holes` : ''}
                            {session.wager ? ` · $${Number(session.wager)}/game` : ''}
                          </div>
                        </div>
                      </div>

                      {/* Matchups */}
                      {groups.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          {groups.map(group => {
                            // Find the game for this group to filter players to only those in this tee time
                            const groupGame = (group.game_id ? allGames.find(g => g.id === group.game_id) : null)
                              || allGames.find(g => g.session_group_id === group.id)
                            const assignment = !groupGame ? (session.game_config?.groupPlayerAssignments || []).find(a => a.groupOrder === group.group_order) : null
                            const groupPlayerIds = groupGame
                              ? new Set((groupGame.game_players || []).map(gp => gp.player_id))
                              : assignment ? new Set(assignment.playerIds) : null
                            const groupTeamsList = (group.session_group_teams || []).map(sgt => {
                              const fullTeam = matchTeams.find(t => t.id === sgt.match_team?.id)
                              const allPlayers = (fullTeam?.match_team_players || []).map(tp => tp.player).filter(Boolean)
                              return {
                                teamName: sgt.match_team?.team_name || 'Team',
                                players: groupPlayerIds ? allPlayers.filter(p => groupPlayerIds.has(p.id)) : allPlayers,
                              }
                            })
                            if (groupTeamsList.length === 0) return null
                            return (
                              <div key={group.id}>
                                <div style={{
                                  fontSize: '0.6875rem',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  color: 'var(--text-muted)',
                                  marginBottom: '0.25rem',
                                }}>
                                  {group.group_name || `Matchup ${(group.group_order ?? 0) + 1}`}
                                </div>
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.04)',
                                  borderRadius: '0.375rem',
                                  border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                                }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.25rem', alignItems: 'start' }}>
                                    {groupTeamsList.map((team, tIdx) => {
                                      const isSecond = tIdx === 1
                                      return (
                                        <div key={tIdx} style={{ display: 'contents' }}>
                                          {tIdx === 1 && (
                                            <div style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              alignSelf: 'center',
                                              fontSize: '0.5625rem',
                                              fontWeight: '700',
                                              color: 'var(--text-muted)',
                                              textTransform: 'uppercase',
                                              padding: '0 0.25rem',
                                            }}>
                                              vs
                                            </div>
                                          )}
                                          <div style={{ textAlign: isSecond ? 'right' : 'left' }}>
                                            <div style={{
                                              fontSize: '0.75rem',
                                              fontWeight: '700',
                                              color: 'var(--accent-primary)',
                                              marginBottom: '0.125rem',
                                            }}>
                                              {team.teamName}
                                            </div>
                                            {team.players.length > 0 && (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', alignItems: isSecond ? 'flex-end' : 'flex-start' }}>
                                                {team.players.map((player) => (
                                                  <span key={player.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexDirection: isSecond ? 'row-reverse' : 'row' }}>
                                                    <span style={{
                                                      width: '1.125rem',
                                                      height: '1.125rem',
                                                      borderRadius: '50%',
                                                      backgroundColor: player.avatar_color,
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      fontWeight: '700',
                                                      fontSize: getInitials(player).length > 1 ? '0.375rem' : '0.5rem',
                                                      color: '#fff',
                                                      flexShrink: 0,
                                                    }}>
                                                      {getInitials(player)}
                                                    </span>
                                                    <span style={{ fontSize: '0.6875rem', fontWeight: '500', color: 'var(--text-secondary)' }}>
                                                      {player.display_name?.split(' ')[0]}
                                                    </span>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Show completed rounds for non-session matches */}
      {!isTeamMatch && completedGames.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem',
          }}>
            Completed Rounds
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {completedGames.map((round, idx) => (
              <RoundItem
                key={round.id}
                round={round}
                index={idx}
                matchPlayers={matchPlayers}
                isOwner={isOwner}
                hasActiveRound={false}
                onStartRound={() => {}}
                onViewRound={(id) => onViewGameId?.(id)}
                showResumeConfirm={showResumeConfirm === round.id}
                onResumeRound={(roundId) => setShowResumeConfirm(roundId)}
                onResumeConfirm={(roundId) => {
                  resumeGame.mutate(roundId, {
                    onSuccess: () => setShowResumeConfirm(null),
                  })
                }}
                onResumeCancel={() => setShowResumeConfirm(null)}
                isResuming={resumeGame.isPending}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Scorecard({ game, matchId, currentHole, onHoleChange, onBackToMatch, onBackToHome, sessionGroups, groupGameMap, selectedGroupId, onSelectGroup, endRoundRequested, onEndRoundHandled }) {
  const { user } = useAuth()
  const { data: fullGame } = useGame(game.id)
  const { data: match } = useMatch(matchId)

  // Persist active game id so ActiveGame can render Scorecard instantly on
  // resume even if useGames() hasn't repopulated yet.
  useEffect(() => {
    if (!matchId || !game.id) return
    try {
      localStorage.setItem(`press_active_game_${matchId}`, game.id)
      localStorage.setItem('press_active_game', game.id)
    } catch { /* ignore */ }
  }, [matchId, game.id])

  // Hydrate currentHole from localStorage on mount so the user lands on the
  // hole they were last entering scores on after a force-kill or long sleep.
  const holeHydratedRef = useRef(false)
  useEffect(() => {
    if (holeHydratedRef.current) return
    holeHydratedRef.current = true
    try {
      const raw = localStorage.getItem(`press_current_hole_${game.id}`)
      const saved = raw ? parseInt(raw, 10) : null
      if (saved && saved >= 1 && saved <= 18 && saved !== currentHole) {
        onHoleChange?.(saved)
      }
    } catch { /* ignore */ }
  }, [game.id])

  // Persist currentHole on every change so resume picks up where the user left off.
  useEffect(() => {
    if (!game.id || !currentHole) return
    try { localStorage.setItem(`press_current_hole_${game.id}`, String(currentHole)) } catch { /* ignore */ }
  }, [game.id, currentHole])

  // Watchdog A: Scorecard load timeout — detect blank/loading screen
  useEffect(() => {
    if (fullGame) return // data already available
    const timer = setTimeout(() => {
      logger.capture(new Error('Scorecard data not available within 5s'), {
        gameId: game.id, hasInitialData: !!getCachedGameData(game.id),
        queueStats: getQueueStats(), isOnline: onlineManager.isOnline(),
      })
    }, 5000)
    return () => clearTimeout(timer)
  }, [!!fullGame, game.id])
  const upsertScore = useUpsertScore()
  const deleteScoreMutation = useDeleteScore()
  const updateGame = useUpdateGame()
  const updateMatchMutation = useUpdateMatch()
  const deleteMatchMutation = useDeleteMatch()
  const deleteGameMutation = useDeleteGame()
  const deactivateSessionMutation = useDeactivateSession()
  const deleteSessionMutation = useDeleteSession()
  const createSettlementsMutation = useCreateSettlements()
  const { data: presses } = usePresses(game.id)
  const createPressMutation = useCreatePress()
  const deletePressMutation = useDeletePress()
  const { data: sideBets } = useSideBets(matchId)
  const { data: ctpResults } = useCtpResults(game.id)
  const upsertCtpResult = useUpsertCtpResult()
  const deleteCtpResult = useDeleteCtpResult()
  const updateScoreTaken = useUpdateScoreTaken()
  const [showCtpPicker, setShowCtpPicker] = useState(null)
  const [noWinnerCtpHoles, setNoWinnerCtpHoles] = useState(new Set())
  const [showCtpWarning, setShowCtpWarning] = useState(false)
  const pendingHoleChange = useRef(null)
  const [editingScore, setEditingScore] = useState(null)
  const scoreAnchorRef = useRef(null)
  const setCurrentHole = (v) => { setEditingScore(null); onHoleChange(v) }
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [scoresComplete, setScoresComplete] = useState(true)
  const [isEndingGame, setIsEndingGame] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [showPressConfirm, setShowPressConfirm] = useState(null)
  const [pendingUndoPress, setPendingUndoPress] = useState(null)
  const [dismissedTriggers, setDismissedTriggers] = useState(new Set())
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(`press_scores_view_${game.id}`)
      if (saved === 'scorecard') return 'scorecard'
    } catch { /* ignore */ }
    return 'entry'
  }) // 'entry' | 'scorecard'
  useEffect(() => {
    try { localStorage.setItem(`press_scores_view_${game.id}`, viewMode) } catch { /* ignore */ }
  }, [viewMode, game.id])
  const [showSettings, setShowSettings] = useState(false)
  const [showMatchupDropdown, setShowMatchupDropdown] = useState(false)

  // Live score updates
  useRealtimeScores(game.id)

  const gameData = fullGame || game
  const isSessionGame = !!gameData.session_group_id
  const isInvitational = match?.match_type === 'invitational'
  const roundLabel = isSessionGame ? (isInvitational ? 'Matchup' : 'Session') : 'Round'
  // Find the session this game belongs to (for deactivation)
  const gameSessionId = useMemo(() => {
    if (!isSessionGame || !match?.sessions) return null
    for (const s of match.sessions) {
      if ((s.session_groups || []).some(g => g.id === gameData.session_group_id)) return s.id
    }
    return null
  }, [isSessionGame, match?.sessions, gameData.session_group_id])
  const queueVer = useQueueVersion() // re-render when queue changes
  const players = gameData.game_players || []
  const serverScores = gameData.scores || []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scores = useMemo(() => mergeScoresWithQueue(serverScores, game.id), [serverScores, game.id, queueVer])
  const numHoles = gameData.num_holes || 18
  const gameTeams = gameData.teams || []

  // On mount, jump to the first hole that doesn't have scores for all players
  // Wait for fullGame to load (scores come from useGame, not the list query)
  const hasInitHole = useRef(false)
  useEffect(() => {
    if (hasInitHole.current || players.length === 0 || !fullGame) return
    hasInitHole.current = true
    const playerIds = players.map(p => p.player_id)
    for (let h = 1; h <= numHoles; h++) {
      const allScored = playerIds.every(pid =>
        scores.some(s => s.player_id === pid && s.hole_number === h && s.strokes != null)
      )
      if (!allScored) {
        if (h !== currentHole) onHoleChange(h)
        return
      }
    }
    // All holes complete — stay on last hole
    if (currentHole !== numHoles) onHoleChange(numHoles)
  }, [players, scores, fullGame]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle "End Round" requested from match dashboard
  useEffect(() => {
    if (!endRoundRequested || !canFinishMatchup || gameData.status === 'completed') return
    const playerIds = players.map(p => p.player_id)
    const allComplete = playerIds.every(pid =>
      Array.from({ length: numHoles }, (_, i) => i + 1).every(h =>
        scores.some(s => s.player_id === pid && s.hole_number === h && s.strokes != null)
      )
    )
    setScoresComplete(allComplete)
    setShowEndConfirm(true)
    onEndRoundHandled?.()
  }, [endRoundRequested]) // eslint-disable-line react-hooks/exhaustive-deps

  // Consolidated scoring hook — single source of truth for handicap, results, match status
  const {
    handicapBreakdown,
    results: roundResults,
    teamStructure,
    dormieSegments,
    matchPlayHoleStatus,
    strokePlayTeamScores,
    course,
    tee,
    numHoles: hookNumHoles,
  } = useGameScoring(gameData, match?.match_players)

  const holePar = course?.par[currentHole - 1] ?? null
  const totalPar = course ? course.par.reduce((s, p) => s + p, 0) : null
  const isSixPack = gameData.game_type === 'six_pack'
  const isStableford = gameData.game_type === 'stableford'
  const isHighLow = gameData.game_type === 'high_low'
  const isScramble = gameData.game_type === 'scramble'
  const isMatchPlay = getGameType(gameData.game_type)?.format === 'match_play'

  const getPlayerHandicap = (playerId) => {
    return handicapBreakdown?.find((h) => h.playerId === playerId) || null
  }

  // Bind shared helpers to local context
  const getStrokesOnHole = (playerId, hole) => getStrokesOnHolePure(handicapBreakdown, playerId, hole)

  // CTP: determine par 3 holes and if CTP bet is active
  const ctpBet = (sideBets || []).find((b) => b.bet_type === 'ctp' && (!b.game_id || b.game_id === game.id))
  const par3Holes = useMemo(() => {
    if (!course) return []
    return course.par
      .map((p, i) => p === 3 ? i + 1 : null)
      .filter((h) => h != null && h <= numHoles)
  }, [course, numHoles])
  const isCurrentHolePar3 = par3Holes.includes(currentHole)
  const currentCtpWinner = (ctpResults || []).find((r) => r.hole_number === currentHole)

  const handleCtpSelect = (playerId) => {
    upsertCtpResult.mutate({ game_id: game.id, hole_number: currentHole, winner_player_id: playerId })
    setNoWinnerCtpHoles(prev => { const next = new Set(prev); next.delete(currentHole); return next })
    setShowCtpPicker(null)
  }

  const handleCtpClear = () => {
    deleteCtpResult.mutate({ gameId: game.id, holeNumber: currentHole })
    setShowCtpPicker(null)
  }

  const handleCtpNoWinner = () => {
    setNoWinnerCtpHoles(prev => new Set(prev).add(currentHole))
    // Clear any existing winner if one was set
    if (currentCtpWinner) {
      deleteCtpResult.mutate({ gameId: game.id, holeNumber: currentHole })
    }
    setShowCtpPicker(null)
  }

  // Navigate forward with CTP warning check
  const navigateNextWithCtpCheck = (targetHoleFn) => {
    if (ctpBet && isCurrentHolePar3 && !currentCtpWinner && !noWinnerCtpHoles.has(currentHole)) {
      pendingHoleChange.current = targetHoleFn
      setShowCtpWarning(true)
      return
    }
    setCurrentHole(targetHoleFn)
  }

  // Skins: compute skins results if a skins bet is active for this round
  const skinsBet = (sideBets || []).find((b) => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && b.game_id === game.id)
  const skinsResult = useMemo(() => {
    if (!skinsBet) return null
    const gamePlayers = (gameData.game_players || []).map((gp) => gp.player_id)

    // Use full course handicap when configured, otherwise use the main game's breakdown
    let skinsHcp = handicapBreakdown
    if (skinsBet.config?.full_handicap && course && tee && match?.match_players) {
      skinsHcp = buildSideBetHandicapBreakdown({
        matchPlayers: match.match_players,
        gamePlayers: gamePlayers.map(pid => ({ player_id: pid })),
        course, tee, mixedTees: getMixedTees(gameData),
      }) || handicapBreakdown
    }
    if (!skinsHcp) return null
    if (gamePlayers.length === 0) return null
    // Overlay in-progress edit so the banner updates immediately
    let effectiveScores = scores
    if (editingScore) {
      const hasExisting = scores.some(s => s.player_id === editingScore.playerId && s.hole_number === editingScore.hole)
      if (hasExisting) {
        effectiveScores = scores.map(s =>
          s.player_id === editingScore.playerId && s.hole_number === editingScore.hole
            ? { ...s, strokes: editingScore.value }
            : s
        )
      } else {
        effectiveScores = [...scores, { player_id: editingScore.playerId, hole_number: editingScore.hole, strokes: editingScore.value }]
      }
    }
    return computeSkinsMoney(effectiveScores, skinsBet, skinsHcp, numHoles, gamePlayers, skinsBet.bet_type === 'skins_carry', skinsBet.config?.gross_tiebreaker)
  }, [skinsBet, handicapBreakdown, gameData.game_players, scores, numHoles, editingScore, course, tee, totalPar, match?.match_players])

  // Permission check: can this user edit scores?
  const isOwner = gameData.created_by === user?.id || match?.owner_id === user?.id
  const isScorer = match?.match_players?.some(
    (mp) => mp.user_id === user?.id && mp.is_scorer
  )
  const isGameScorer = gameData.game_players?.some(
    (gp) => gp.player?.user_id === user?.id && gp.is_scorer
  )
  const isTeamEvent = match?.match_type === 'team_match' || match?.match_type === 'invitational'
  // Team events: only designated game scorers can edit scores for their matchup
  // Standard matches: match owner, match-level scorers, or standalone game creator
  const canEditScores = gameData.status !== 'completed' && (
    isTeamEvent
      ? isGameScorer
      : (isOwner || isScorer || !matchId)
  )
  // Invitationals: game scorer OR match owner can finish a matchup; others: match owner finishes
  const canFinishMatchup = isSessionGame && isInvitational ? (isGameScorer || isOwner) : isOwner
  // Any match member can press
  const isMatchMember = match?.match_players?.some((mp) => mp.user_id === user?.id)
  const canPress = canEditScores || isMatchMember

  const getScore = (playerId, hole) => getScorePure(scores, playerId, hole)
  const getTotal = (playerId) => getTotalPure(scores, playerId)
  const getNetTotal = (playerId) => getNetTotalPure(scores, handicapBreakdown, playerId)
  const getScoreTaken = (playerId, hole) => getScoreTakenPure(scores, playerId, hole)

  const handleSixPackToggle = (playerId, hole) => {
    if (!canEditScores) return
    const current = getScoreTaken(playerId, hole)
    // null/true = taken (default), false = dropped
    const newVal = current === false ? true : false
    updateScoreTaken.mutate({
      game_id: game.id,
      player_id: playerId,
      hole_number: hole,
      taken: newVal,
    })
  }

  // Count score taken per player per nine (Six Pack)
  const getSixPackStats = (teamPlayerIds, startHole, endHole) => {
    let taken = 0
    let totalWithScores = 0
    for (let h = startHole; h <= endHole; h++) {
      for (const pid of teamPlayerIds) {
        const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === h && sc.strokes != null)
        if (s) {
          totalWithScores++
          if (s.score_taken !== false) taken++
        }
      }
    }
    const holesRemaining = endHole - Math.max(
      ...teamPlayerIds.map((pid) => {
        const playerScores = scores.filter((sc) => sc.player_id === pid && sc.hole_number >= startHole && sc.hole_number <= endHole && sc.strokes != null)
        return playerScores.length > 0 ? Math.max(...playerScores.map((s) => s.hole_number)) : startHole - 1
      }),
      startHole - 1
    )
    const dropped = totalWithScores - taken
    const mustDrop = 6 // must drop 6 of 18 per nine
    return { taken, dropped, needToDrop: Math.max(0, mustDrop - dropped), holesRemaining }
  }

  const handleScoreTap = async (playerId, hole) => {
    if (!canEditScores) return
    // Save any pending edit before switching or closing
    if (editingScore && editingScore.value != null) {
      upsertScore.mutate({
        game_id: game.id,
        player_id: editingScore.playerId,
        hole_number: editingScore.hole,
        strokes: editingScore.value,
      })
    }
    const existing = getScore(playerId, hole)
    if (editingScore?.playerId === playerId && editingScore?.hole === hole) {
      setEditingScore(null)
      return
    }
    if (existing != null) {
      setEditingScore({ playerId, hole, value: existing, initialValue: existing })
    } else {
      // Default to par for the hole — don't save until user confirms
      const defaultVal = course?.par[hole - 1] || 4
      setEditingScore({ playerId, hole, value: defaultVal, initialValue: null })
    }
  }

  const saveScoreAndSixPack = async (playerId, hole, strokes) => {
    const scoreStart = Date.now()
    try {
      // Scramble: write the same score to ALL players on the team
      if (isScramble) {
        const teamGroup = playersByTeam.find(g =>
          g.players.some(gp => (gp.player?.id || gp.player_id) === playerId)
        )
        if (teamGroup) {
          const teamPlayerIds = teamGroup.players.map(gp => gp.player?.id || gp.player_id).filter(Boolean)
          await Promise.all(teamPlayerIds.map(pid =>
            upsertScore.mutateAsync({
              game_id: game.id,
              player_id: pid,
              hole_number: hole,
              strokes,
            })
          ))
        }
      } else {
        await upsertScore.mutateAsync({
          game_id: game.id,
          player_id: playerId,
          hole_number: hole,
          strokes,
        })
      }
      // Watchdog C: Score entry latency
      const elapsed = Date.now() - scoreStart
      if (elapsed > 3000) {
        logger.capture(new Error('Score entry took >3s to appear'), {
          elapsed, gameId: game.id, isOnline: onlineManager.isOnline(),
          queueStats: getQueueStats(),
        })
      }
      if (isSixPack) {
        const teamGroup = playersByTeam.find(g =>
          g.players.some(gp => (gp.player?.id || gp.player_id) === playerId)
        )
        if (teamGroup) {
          const nineStart = hole <= 9 ? 1 : 10
          const nineEnd = hole <= 9 ? 9 : numHoles
          const teamPlayerIds = teamGroup.players.map(gp => gp.player?.id || gp.player_id).filter(Boolean)
          const stats = getSixPackStats(teamPlayerIds, nineStart, nineEnd)
          if (stats.taken >= 12) {
            await updateScoreTaken.mutateAsync({
              game_id: game.id,
              player_id: playerId,
              hole_number: hole,
              taken: false,
            })
          }
        }
      }
      // No explicit refetch — sync engine + realtime handle server state updates
    } catch {
      // Score is safely in the localStorage queue; sync engine will retry
    }
  }

  const handleScoreStep = (delta) => {
    if (!editingScore) return
    const next = Math.max(1, Math.min(20, editingScore.value + delta))
    if (next === editingScore.value) return
    setEditingScore({ ...editingScore, value: next })
  }

  const handleScoreClear = () => {
    if (!editingScore) return
    if (editingScore.initialValue != null) {
      // Scramble: delete score for ALL players on the team
      if (isScramble) {
        const teamGroup = playersByTeam.find(g =>
          g.players.some(gp => (gp.player?.id || gp.player_id) === editingScore.playerId)
        )
        if (teamGroup) {
          teamGroup.players.forEach(gp => {
            const pid = gp.player?.id || gp.player_id
            deleteScoreMutation.mutate({
              game_id: game.id,
              player_id: pid,
              hole_number: editingScore.hole,
            })
          })
        }
      } else {
        deleteScoreMutation.mutate({
          game_id: game.id,
          player_id: editingScore.playerId,
          hole_number: editingScore.hole,
        })
      }
    }
    setEditingScore(null)
  }

  // roundResults, teamStructure, handicapBreakdown, dormieSegments come from useGameScoring above

  // Press system: read config and compute triggers
  const pressConfig = gameData?.game_config?.pressConfig || null
  const pressTriggers = useMemo(() => {
    if (!pressConfig || !roundResults) return []
    return getPressTriggers(roundResults, pressConfig, presses || [], currentHole, numHoles, gameData?.game_config)
  }, [roundResults, pressConfig, presses, currentHole, numHoles, gameData?.game_config])

  // Clear dismissed triggers whenever scoring changes (new hole scored, score edited, etc.)
  // so triggers reappear with updated deficit info after each hole.
  // Use a fingerprint string so dismissals aren't cleared on unrelated re-renders.
  const triggersFingerprint = useMemo(() =>
    pressTriggers.map(t => `${t.segment}:${t.deficit}:${t.pressStartHole}:${t.parentPressId || ''}`).join('|'),
    [pressTriggers]
  )
  const prevFingerprintRef = useRef(triggersFingerprint)
  useEffect(() => {
    if (prevFingerprintRef.current !== triggersFingerprint) {
      prevFingerprintRef.current = triggersFingerprint
      if (dismissedTriggers.size > 0) {
        setDismissedTriggers(new Set())
      }
    }
  }, [triggersFingerprint]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ticker: emit events when a hole becomes fully scored (all players have scores)
  const emitTickerEvents = useEmitTickerEvents(matchId)
  const emittedHolesRef = useRef(new Set())
  const prevResultsRef = useRef(null)

  // Compute which holes are fully scored
  const completedHoles = useMemo(() => {
    if (!roundResults?.holeResults) return new Set()
    return new Set(
      roundResults.holeResults
        .filter((hr) => !hr.pending)
        .map((hr) => hr.hole)
    )
  }, [roundResults])

  useEffect(() => {
    if (!matchId || !game.id || !course) return
    if (!canEditScores) return

    // Find newly completed holes (scored but not yet emitted)
    const newHoles = []
    for (const hole of completedHoles) {
      if (!emittedHolesRef.current.has(hole)) {
        newHoles.push(hole)
        emittedHolesRef.current.add(hole)
      }
    }
    if (newHoles.length === 0) return

    const prevResults = prevResultsRef.current
    prevResultsRef.current = roundResults ? JSON.parse(JSON.stringify(roundResults, (_, v) => v instanceof Map ? Object.fromEntries(v) : v)) : null

    const allEvents = []

    // Only prefix with matchup name when there are multiple groups
    const matchupGroup = gameData.session_group_id && sessionGroups.length > 1
      ? sessionGroups.find((g) => g.id === gameData.session_group_id)
      : null
    const gameName = matchupGroup
      ? (matchupGroup.group_name || `Matchup ${(matchupGroup.group_order ?? 0) + 1}`)
      : null

    for (const hole of newHoles) {
      const par = course.par[hole - 1]

      // Score events (ace) for every player on this hole
      for (const gp of players) {
        const pid = gp.player?.id || gp.player_id
        const playerName = gp.player?.display_name || 'Unknown'
        const gross = scores.find((s) => s.player_id === pid && s.hole_number === hole)?.strokes
        if (gross == null) continue

        allEvents.push(...detectScoreEvents({
          gameId: game.id,
          matchId,
          playerId: pid,
          playerName,
          holeNumber: hole,
          grossScore: gross,
          par,
        }))
      }
    }

    // Match events use the latest completed hole for comparison
    const latestHole = Math.max(...newHoles)
    if (roundResults && teamStructure.length >= 2) {
      allEvents.push(...detectMatchEvents({
        prevResults,
        currResults: roundResults,
        gameId: game.id,
        matchId,
        holeNumber: latestHole,
        teams: teamStructure,
        gameName,
      }))
      allEvents.push(...detectClinchEvents({
        prevResults,
        currResults: roundResults,
        gameId: game.id,
        matchId,
        holeNumber: latestHole,
        teams: teamStructure,
        gameName,
      }))
      allEvents.push(...detectDormieEvents({
        prevResults,
        currResults: roundResults,
        gameId: game.id,
        matchId,
        holeNumber: latestHole,
        teams: teamStructure,
        gameName,
      }))

      // Score summary for the ticker
      const summary = buildScoreSummary({
        roundResults,
        gameId: game.id,
        matchId,
        holeNumber: latestHole,
        teams: teamStructure,
        gameName,
      })
      if (summary) allEvents.push(summary)
    }

    if (allEvents.length > 0) {
      emitTickerEvents.mutate(allEvents)
      // Dispatch high-impact events locally for immediate toast display
      for (const event of allEvents) {
        window.dispatchEvent(new CustomEvent('ticker-toast', { detail: event }))
      }
    }
  }, [completedHoles]) // eslint-disable-line react-hooks/exhaustive-deps

  // Watchdog H: Round end timeout — fires if handleEndGame doesn't complete within 15s
  const endGameWatchdog = useRef(null)

  const handleEndGame = async () => {
    if (!canFinishMatchup) return
    if (!onlineManager.isOnline()) {
      logger.capture(new Error('Round end blocked — offline'), {
        gameId: game?.id, matchId, isSessionGame,
      })
      alert('Unable to end round — no network connection. Please try again when connectivity is restored.')
      return
    }
    setIsEndingGame(true)

    // Start watchdog timer
    clearTimeout(endGameWatchdog.current)
    endGameWatchdog.current = setTimeout(() => {
      logger.capture(new Error('Watchdog H: Round end timeout — handleEndGame did not complete within 15s'), {
        gameId: game?.id,
        matchId,
        isSessionGame,
        isOnline: navigator.onLine,
      })
    }, 15000)

    try {
    let moneyResults = []
    let pressDataArr = []

    // Generate settlements from round results
    if (roundResults && teamStructure.length >= 2 && gameData.wager > 0) {
      pressDataArr = (presses || []).map((p) => ({
        segment: p.metadata?.segment,
        startHole: p.hole_start,
        wager: Number(p.amount),
        pressing_team: p.metadata?.pressing_team,
      }))
      moneyResults = computeMoneyResults(roundResults, teamStructure, Number(gameData.wager), gameData.game_type, pressDataArr, { nassauWeights: gameData.game_config?.nassauWeights })

      // Convert money results to payer→payee settlements
      const settlements = []
      const winners = moneyResults.filter((m) => m.amount > 0)
      const losers = moneyResults.filter((m) => m.amount < 0)

      // Each loser pays each winner proportionally
      for (const loser of losers) {
        const loserOwes = Math.abs(loser.amount)
        const totalWinnings = winners.reduce((s, w) => s + w.amount, 0)

        for (const winner of winners) {
          if (totalWinnings === 0) continue
          const share = (winner.amount / totalWinnings) * loserOwes
          if (share > 0.005) { // skip dust amounts
            settlements.push({
              payerId: loser.playerId,
              payeeId: winner.playerId,
              amount: Math.round(share * 100) / 100,
            })
          }
        }
      }

      if (settlements.length > 0) {
        try {
          await createSettlementsMutation.mutateAsync({ gameId: game.id, settlements })
        } catch (err) {
          logger.error('Failed to create settlements: ' + err?.message)
        }
      }
    }

    // Build side bet snapshots
    const sideBetSnapshot = {}
    const gamePlayerIds = players.map(gp => gp.player?.id || gp.player_id).filter(Boolean)

    // CTP
    if (ctpBet && course && ctpResults?.length > 0 && par3Holes.length > 0) {
      sideBetSnapshot.ctp = computeCtpMoney(ctpResults, ctpBet, gamePlayerIds, par3Holes)
    }

    // Skins
    if (skinsBet) {
      let snapshotSkinsHcp = handicapBreakdown
      if (skinsBet.config?.full_handicap && course && tee && match?.match_players) {
        snapshotSkinsHcp = buildSideBetHandicapBreakdown({
          matchPlayers: match.match_players, gamePlayers: players, course, tee, mixedTees: getMixedTees(gameData),
        }) || handicapBreakdown
      }
      if (snapshotSkinsHcp) {
        sideBetSnapshot.skins = computeSkinsMoney(scores, skinsBet, snapshotSkinsHcp, numHoles, gamePlayerIds, skinsBet.bet_type === 'skins_carry', skinsBet.config?.gross_tiebreaker)
      }
    }

    // Round Low Net
    const roundLowNetBet = (sideBets || []).find(b => b.bet_type === 'low_net' && b.game_id === game.id && b.scope === 'round')
    if (roundLowNetBet && course && tee) {
      const lowNetHcp = buildSideBetHandicapBreakdown({
        matchPlayers: match?.match_players, gamePlayers: players, course, tee, mixedTees: getMixedTees(gameData),
      })
      if (lowNetHcp) {
        sideBetSnapshot.lowNet = computeLowNetMoney(scores, roundLowNetBet, lowNetHcp, numHoles, gamePlayerIds)
      }
    }

    // Build snapshot of all results
    const snapshot = buildRoundSnapshot({
      roundResults,
      moneyResults,
      handicapBreakdown: handicapBreakdown,
      teamStructure,
      sideBets: sideBetSnapshot,
      pressData: pressDataArr,
    })

    // For session games, save snapshot for current game + handle session/matchup completion
    if (isSessionGame && gameSessionId) {
      if (isInvitational) {
        // INVITATIONAL: Only complete THIS matchup's game — other matchups finish independently
        await updateGame.mutateAsync({
          id: game.id,
          status: 'completed',
          completed_at: new Date().toISOString(),
          final_results: snapshot,
        })
      } else {
      // TEAM_MATCH: Save snapshot for the current game, process all other matchups, then deactivate session
      await updateGame.mutateAsync({
        id: game.id,
        final_results: snapshot,
      })

      // Process other matchup games in this session (settlements + snapshots)
      if (groupGameMap) {
        const otherGameIds = Object.values(groupGameMap)
          .map(g => g.id)
          .filter(id => id !== game.id)

        const matchSideBets = await fetchSideBetsApi(matchId)

        for (const otherGameId of otherGameIds) {
          try {
            const otherGame = await fetchGameApi(otherGameId)
            if (!otherGame || otherGame.status === 'completed') continue

            const otherPlayers = otherGame.game_players || []
            const otherScores = otherGame.scores || []
            const otherTeams = (otherGame.teams || [])
              .slice().sort((a, b) => a.team_order - b.team_order)
            const otherTeamStructure = otherTeams.map(t => ({
              playerIds: (t.team_players || []).map(tp => tp.player_id),
            }))
            const otherCourse = getGameCourse(otherGame)
            const otherTee = getGameTee(otherGame)
            const otherNumHoles = otherGame.num_holes || 18
            const otherGamePlayerIds = otherPlayers.map(gp => gp.player?.id || gp.player_id).filter(Boolean)

            const otherHcpBreakdown = buildGameHandicapBreakdown({
              gameType: otherGame.game_type,
              teamStructure: otherTeamStructure,
              matchPlayers: match?.match_players,
              gamePlayers: otherPlayers,
              course: otherCourse,
              tee: otherTee,
              mixedTees: getMixedTees(otherGame),
            })

            const otherRoundResults = otherHcpBreakdown
              ? computeRoundResults(otherGame.game_type, otherTeamStructure, otherScores, otherHcpBreakdown, otherNumHoles, otherGame.game_config)
              : null

            let otherMoneyResults = []
            let otherPressData = []

            if (otherRoundResults && otherTeamStructure.length >= 2 && otherGame.wager > 0) {
              const otherPresses = (otherGame.bets || []).filter(b => b.bet_type === 'press')
              otherPressData = otherPresses.map(p => ({
                segment: p.metadata?.segment,
                startHole: p.hole_start,
                wager: Number(p.amount),
                pressing_team: p.metadata?.pressing_team,
              }))
              otherMoneyResults = computeMoneyResults(otherRoundResults, otherTeamStructure, Number(otherGame.wager), otherGame.game_type, otherPressData, { nassauWeights: otherGame.game_config?.nassauWeights })

              // Create settlements
              const otherSettlements = []
              const otherWinners = otherMoneyResults.filter(m => m.amount > 0)
              const otherLosers = otherMoneyResults.filter(m => m.amount < 0)
              for (const loser of otherLosers) {
                const loserOwes = Math.abs(loser.amount)
                const totalWinnings = otherWinners.reduce((s, w) => s + w.amount, 0)
                for (const winner of otherWinners) {
                  if (totalWinnings === 0) continue
                  const share = (winner.amount / totalWinnings) * loserOwes
                  if (share > 0.005) {
                    otherSettlements.push({ payerId: loser.playerId, payeeId: winner.playerId, amount: Math.round(share * 100) / 100 })
                  }
                }
              }
              if (otherSettlements.length > 0) {
                await createSettlementsApi(otherGameId, otherSettlements)
              }
            }

            // Build side bet snapshots for the other game
            const otherSideBetSnapshot = {}
            const otherCtpBet = (matchSideBets || []).find(b => b.bet_type === 'ctp' && b.game_id === otherGameId)
            const otherCtpResults = otherGame.ctp_results || []
            if (otherCtpBet && otherCourse && otherCtpResults.length > 0) {
              const otherPar3s = otherCourse.par
                .map((p, i) => p === 3 ? i + 1 : null)
                .filter(h => h != null && h <= otherNumHoles)
              if (otherPar3s.length > 0) {
                otherSideBetSnapshot.ctp = computeCtpMoney(otherCtpResults, otherCtpBet, otherGamePlayerIds, otherPar3s)
              }
            }
            const otherSkinsBet = (matchSideBets || []).find(b => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && b.game_id === otherGameId)
            if (otherSkinsBet && otherHcpBreakdown) {
              otherSideBetSnapshot.skins = computeSkinsMoney(otherScores, otherSkinsBet, otherHcpBreakdown, otherNumHoles, otherGamePlayerIds, otherSkinsBet.bet_type === 'skins_carry', otherSkinsBet.config?.gross_tiebreaker)
            }
            const otherLowNetBet = (matchSideBets || []).find(b => b.bet_type === 'low_net' && b.game_id === otherGameId && b.scope === 'round')
            if (otherLowNetBet && otherCourse && otherTee) {
              const otherLowNetHcp = buildSideBetHandicapBreakdown({
                matchPlayers: match?.match_players, gamePlayers: otherPlayers,
                course: otherCourse, tee: otherTee, mixedTees: getMixedTees(otherGame),
              })
              if (otherLowNetHcp) {
                otherSideBetSnapshot.lowNet = computeLowNetMoney(otherScores, otherLowNetBet, otherLowNetHcp, otherNumHoles, otherGamePlayerIds)
              }
            }

            // Save snapshot for the other game
            const otherSnapshot = buildRoundSnapshot({
              roundResults: otherRoundResults,
              moneyResults: otherMoneyResults,
              handicapBreakdown: otherHcpBreakdown,
              teamStructure: otherTeamStructure,
              sideBets: otherSideBetSnapshot,
              pressData: otherPressData,
            })
            await updateGameApi(otherGameId, { final_results: otherSnapshot })
          } catch (err) {
            logger.error(`Failed to process session game ${otherGameId}: ${err?.message}`)
          }
        }
      }

      await deactivateSessionMutation.mutateAsync({ id: gameSessionId, match_id: matchId, mode: 'complete' })
      }
    } else {
      // Mark game as completed with locked results
      updateGame.mutate({
        id: game.id,
        status: 'completed',
        completed_at: new Date().toISOString(),
        final_results: snapshot,
      })
    }

    // For quick_round and team_match, also complete the match so user doesn't have to do it separately
    // (team matches are single-session by design)
    if ((match?.match_type === 'quick_round' || match?.match_type === 'team_match') && matchId) {
      updateMatchMutation.mutate({ id: matchId, status: 'completed' })
    }

    clearTimeout(endGameWatchdog.current)
    setIsEndingGame(false)
    setShowEndConfirm(false)

    // Navigate away: quick_round and team_match go home, regular match goes to dashboard
    if (match?.match_type === 'quick_round' || match?.match_type === 'team_match') {
      onBackToHome?.()
    } else {
      onBackToMatch?.()
    }
    } catch (err) {
      clearTimeout(endGameWatchdog.current)
      logger.capture(new Error('Round end failed: ' + err?.message), {
        gameId: game?.id,
        matchId,
        isSessionGame,
        isOnline: onlineManager.isOnline(),
      })
      setIsEndingGame(false)
      alert('Failed to end round. Please check your connection and try again.')
    }
  }

  const handlePress = (triggerOrDormie) => {
    if (!canPress) return
    const segment = triggerOrDormie.segment
    const segStart = segment === 'back' ? 10 : 1
    const segEnd = segment === 'front' ? 9 : numHoles

    // Use pressStartHole from trigger if available, otherwise compute it
    let pressStartHole = triggerOrDormie.pressStartHole
    if (!pressStartHole) {
      pressStartHole = segStart
      for (let h = segStart; h <= segEnd; h++) {
        const allScored = players.every(({ player }) =>
          scores.some((s) => s.player_id === player?.id && s.hole_number === h && s.strokes != null)
        )
        if (!allScored) {
          pressStartHole = h
          break
        }
      }
    }

    createPressMutation.mutate({
      game_id: game.id,
      segment,
      start_hole: pressStartHole,
      end_hole: segEnd,
      wager: Number(gameData.wager) || 0,
      pressing_team: triggerOrDormie.losingTeam,
      parent_press_id: triggerOrDormie.parentPressId || null,
      trigger_type: 'manual',
    })
  }

  // Group players by team for display
  const playersByTeam = useMemo(() => {
    if (gameTeams.length === 0) {
      return [{ name: null, players }]
    }
    return gameTeams
      .sort((a, b) => a.team_order - b.team_order)
      .map((team) => {
        const teamPlayerIds = (team.team_players || []).map((tp) => tp.player_id)
        const teamPlayers = players.filter((gp) => teamPlayerIds.includes(gp.player_id))
        return { name: team.team_name, players: teamPlayers }
      })
  }, [gameTeams, players])

  const hasTeams = playersByTeam.length > 1
  const teamColors = ['var(--accent-primary)', 'var(--accent-secondary, #f59e0b)', 'var(--accent-danger, #ef4444)', '#8b5cf6']

  // matchPlayHoleStatus comes from useGameScoring above

  const pressHoleStatuses = useMemo(() => {
    return computePressHoleStatuses(roundResults, presses, teamStructure)
  }, [roundResults, presses, teamStructure])

  // Determine hole winner for current hole (match play team highlighting)
  const currentHoleResult = roundResults?.holeResults?.find(hr => hr.hole === currentHole)
  const holeWinnerIdx = (currentHoleResult && !currentHoleResult.pending) ? currentHoleResult.winner : null

  return (
    <div>
      {/* View mode toggle */}
      <div style={{
        display: 'flex',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '0.5rem',
        padding: '0.25rem',
        marginBottom: '1rem',
      }}>
        <button
          onClick={() => setViewMode('entry')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            backgroundColor: viewMode === 'entry' ? 'var(--bg-secondary)' : 'transparent',
            color: viewMode === 'entry' ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: viewMode === 'entry' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
          }}
        >
          <PenLine size={14} />
          Hole-by-Hole
        </button>
        <button
          onClick={() => setViewMode('scorecard')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            backgroundColor: viewMode === 'scorecard' ? 'var(--bg-secondary)' : 'transparent',
            color: viewMode === 'scorecard' ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: viewMode === 'scorecard' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
          }}
        >
          <Grid3X3 size={14} />
          Scorecard
        </button>
      </div>

      {/* Matchup selector — full width, above game header */}
      {sessionGroups && sessionGroups.length > 1 && onSelectGroup && (
        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
          <button
            onClick={() => setShowMatchupDropdown(!showMatchupDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '0.625rem',
              borderRadius: '9999px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              fontSize: '0.9375rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            {sessionGroups.find(g => g.id === selectedGroupId)?.group_name || gameData.game_name || 'Matchup'}
            <ChevronDown size={16} style={{ transition: 'transform 0.15s', transform: showMatchupDropdown ? 'rotate(180deg)' : 'none' }} />
          </button>
          {showMatchupDropdown && (
            <>
              <div onClick={() => setShowMatchupDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 0.25rem)',
                left: 0,
                right: 0,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                zIndex: 100,
                overflow: 'hidden',
              }}>
                {sessionGroups.map(group => {
                  const isSelected = group.id === selectedGroupId
                  const groupGame = groupGameMap?.[group.id]
                  const playerCount = groupGame?.game_players?.length
                  return (
                    <button
                      key={group.id}
                      onClick={() => { onSelectGroup(group.id); setShowMatchupDropdown(false) }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? '#10b981' : 'var(--text-primary)',
                        backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span>{group.group_name}</span>
                      {playerCount && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{playerCount} players</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Game header */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            {!(sessionGroups && sessionGroups.length > 1) && (
              <h2 style={{ fontSize: '1rem', fontWeight: '700' }}>
                {gameData.game_name || gameData.course_name || 'Current Round'}
              </h2>
            )}
            {gameData.course_name && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginTop: '0.125rem', display: 'block' }}>
                {gameData.course_name}
              </span>
            )}
            {tee && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: '0.125rem', display: 'block' }}>
                {tee.name} &middot; {tee.totalYardage?.toLocaleString()} yds &middot; Slope {tee.slope}
              </span>
            )}
          </div>
          {isOwner && !isSessionGame && (
            <button
              onClick={() => setShowSettings(true)}
              title="Round Settings"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-muted)',
                flexShrink: 0,
              }}
            >
              <Pencil size={18} />
            </button>
          )}
        </div>
        {gameData.game_type && gameData.game_type !== 'stroke' && (
          <div style={{ marginTop: '0.5rem' }}>
            <GameTypeBadge gameTypeId={gameData.game_type} />
          </div>
        )}
      </div>

      {/* Round Settings Editor */}
      {showSettings && (
        <RoundSettingsEditor
          game={gameData}
          matchId={matchId}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* CTP warning modal — no winner selected on a par 3 */}
      {showCtpWarning && (
        <div
          onClick={() => setShowCtpWarning(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '1rem',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '340px',
              textAlign: 'center',
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'rgba(var(--accent-secondary-rgb, 245, 158, 11), 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
            }}>
              <Target size={24} style={{ color: 'var(--accent-secondary, #f59e0b)' }} />
            </div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem' }}>
              CTP Not Selected
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem', lineHeight: 1.4 }}>
              Hole {currentHole} is a par 3 with a Closest to the Pin bet. No winner has been selected yet.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  setShowCtpWarning(false)
                  setShowCtpPicker(currentHole)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#fff',
                  backgroundColor: 'var(--accent-secondary, #f59e0b)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Pick CTP Winner
              </button>
              <button
                onClick={() => {
                  setShowCtpWarning(false)
                  if (pendingHoleChange.current) {
                    setCurrentHole(pendingHoleChange.current)
                    pendingHoleChange.current = null
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  cursor: 'pointer',
                }}
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End round confirmation modal */}
      {showEndConfirm && (
        <div
          onClick={(isEndingGame || updateGame.isPending || deactivateSessionMutation.isPending || createSettlementsMutation.isPending) ? undefined : () => setShowEndConfirm(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            {(isEndingGame || updateGame.isPending || deactivateSessionMutation.isPending || createSettlementsMutation.isPending) ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '1rem 0' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid var(--border-color)',
                  borderTopColor: 'var(--accent-primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <p style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--text-secondary)', textAlign: 'center' }}>
                  Finishing {roundLabel}...
                </p>
              </div>
            ) : (
              <>
                {!scoresComplete && (
                  <div style={{
                    backgroundColor: 'rgba(234, 179, 8, 0.12)',
                    border: '1px solid rgba(234, 179, 8, 0.3)',
                    borderRadius: '0.75rem',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                  }}>
                    <AlertTriangle size={16} style={{ color: '#eab308', flexShrink: 0 }} />
                    <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#eab308', margin: 0 }}>
                      Missing Scores
                    </p>
                  </div>
                )}
                <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
                  {scoresComplete ? `End this ${roundLabel.toLowerCase()}?` : `End with incomplete scores?`}
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', textAlign: 'center', lineHeight: 1.4 }}>
                  {scoresComplete
                    ? 'Final scores will be locked and settlements will be auto-generated based on results.'
                    : 'Missing scores will count as zero for settlements.'}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {!scoresComplete && matchId && (
                    <button
                      onClick={() => {
                        setShowEndConfirm(false)
                        setShowDiscardConfirm(true)
                      }}
                      style={{
                        height: '44px',
                        width: '100%',
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        borderRadius: '9999px',
                        backgroundColor: 'transparent',
                        border: '2px solid #ef4444',
                        color: '#ef4444',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <Trash2 size={16} />
                      Discard {roundLabel}
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    onClick={handleEndGame}
                    style={{ height: '44px', width: '100%', fontSize: '0.9375rem', fontWeight: '600', borderRadius: '9999px' }}
                  >
                    {scoresComplete ? `Confirm End ${roundLabel}` : `End Anyway`}
                  </button>
                  <button
                    onClick={() => setShowEndConfirm(false)}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '9999px',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {scoresComplete ? 'Cancel' : 'Go Back'}
                  </button>
                </div>
                {scoresComplete && matchId && (
                  <button
                    onClick={() => {
                      setShowEndConfirm(false)
                      setShowDiscardConfirm(true)
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      marginTop: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem',
                      width: '100%',
                      opacity: 0.7,
                    }}
                  >
                    <Trash2 size={12} />
                    Discard {roundLabel}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Discard round confirmation modal */}
      {showDiscardConfirm && (
        <div
          onClick={() => setShowDiscardConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              Delete {roundLabel}?
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              This will permanently delete this {roundLabel.toLowerCase()}, all scores, and settlements. This cannot be undone.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  if (!onlineManager.isOnline()) {
                    logger.capture(new Error('Round discard blocked — offline'), {
                      gameId: game?.id, matchId, matchType: match?.match_type, isSessionGame,
                    })
                    alert('Unable to discard — no network connection. Please try again when connectivity is restored.')
                    return
                  }
                  const errorCallbacks = {
                    onError: (err) => {
                      logger.capture(new Error('Round discard failed: ' + err?.message), {
                        gameId: game?.id, matchId, matchType: match?.match_type, isSessionGame,
                        isOnline: onlineManager.isOnline(),
                      })
                      alert('Failed to discard. Please check your connection and try again.')
                    },
                  }
                  if (match?.match_type === 'quick_round') {
                    deleteMatchMutation.mutate(matchId, { onSuccess: () => { setShowDiscardConfirm(false); onBackToHome?.() }, ...errorCallbacks })
                  } else if (isSessionGame && gameSessionId) {
                    // Discard session games, then delete the session entirely
                    ;(async () => {
                      try {
                        await deactivateSessionMutation.mutateAsync({ id: gameSessionId, match_id: matchId, mode: 'discard' })
                        await deleteSessionMutation.mutateAsync({ id: gameSessionId, match_id: matchId })
                        setShowDiscardConfirm(false)
                        onBackToMatch?.()
                      } catch (err) {
                        logger.capture(new Error('Round discard failed: ' + err?.message), {
                          gameId: game?.id, matchId, matchType: match?.match_type, isSessionGame,
                          isOnline: onlineManager.isOnline(),
                        })
                        alert('Failed to discard. Please check your connection and try again.')
                      }
                    })()
                  } else {
                    deleteGameMutation.mutate({ gameId: game.id, matchId }, { onSuccess: () => { setShowDiscardConfirm(false); onBackToMatch?.() }, ...errorCallbacks })
                  }
                }}
                disabled={deleteMatchMutation.isPending || deleteGameMutation.isPending || deactivateSessionMutation.isPending || deleteSessionMutation.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {deleteMatchMutation.isPending || deleteGameMutation.isPending || deactivateSessionMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDiscardConfirm(false)}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Press trigger alerts — only show UNACTIVATED triggers here (activated ones shown in Presses section below) */}
      {(() => {
        // Only show triggers from the configured press system (no dormie-only fallback)
        const triggersToShow = pressConfig ? pressTriggers : []

        // Only show triggers that have NOT been pressed yet and not dismissed
        const unactivatedTriggers = triggersToShow.filter(t => {
          const triggerKey = `${t.segment}-${t.parentPressId || 'base'}`
          if (dismissedTriggers.has(triggerKey)) return false
          const isRepress = !!t.parentPressId
          const alreadyPressed = presses?.some(p => {
            if (isRepress) return p.metadata?.parent_press_id === t.parentPressId && p.hole_start === t.pressStartHole
            return p.metadata?.segment === t.segment && !p.metadata?.parent_press_id
          })
          return !alreadyPressed
        })

        return unactivatedTriggers.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1rem' }}>
            {unactivatedTriggers.map((t) => {
              const segLabel = t.segment === 'front' ? 'Front' : t.segment === 'back' ? 'Back' : 'Total'
              const losingTeamName = t.losingTeam != null ? (teamStructure[t.losingTeam]?.name || `Team ${t.losingTeam + 1}`) : null
              const winningTeamIdx = t.losingTeam != null ? (t.losingTeam === 0 ? 1 : 0) : null
              const winningTeamName = winningTeamIdx != null ? (teamStructure[winningTeamIdx]?.name || `Team ${winningTeamIdx + 1}`) : null
              const triggerKey = `${t.segment}-${t.parentPressId || 'base'}`

              return (
                <div
                  key={triggerKey}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: t.isDormie ? 'rgba(239, 68, 68, 0.1)' : t.isLastHolePress ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.08)',
                    borderRadius: '0.75rem',
                    border: `1px solid ${t.isDormie ? 'rgba(239, 68, 68, 0.3)' : t.isLastHolePress ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{
                        fontSize: '0.6875rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: t.isDormie ? 'var(--accent-danger)' : 'var(--accent-warning)',
                        backgroundColor: t.isDormie ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                        padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                      }}>
                        {segLabel}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: '1.3' }}>
                      {t.isDormie ? (
                        <><span style={{ fontWeight: '800', color: 'var(--accent-danger)' }}>{winningTeamName} Dormie!</span>{' '}Press remaining holes?</>
                      ) : t.isLastHolePress ? (
                        <>Either team can press the remaining holes</>
                      ) : (
                        <>
                          {losingTeamName || 'Team'} is{' '}
                          <span style={{
                            fontWeight: '800', color: 'var(--accent-danger)',
                            padding: '0.0625rem 0.3125rem', borderRadius: '0.25rem',
                            boxShadow: 'inset 0 0 0 2px var(--accent-danger)',
                          }}>
                            {t.deficit} {roundResults?.type === 'match_play' ? 'DN' : 'behind'}
                          </span>
                          {' '}Press last {t.remaining} hole{t.remaining !== 1 ? 's' : ''}?
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
                    {canPress && (
                      <button
                        onClick={() => setShowPressConfirm(t)}
                        style={{
                          padding: '0.625rem 1rem', fontSize: '0.875rem', fontWeight: '800', color: 'white',
                          backgroundColor: 'var(--accent-warning)',
                          borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0,
                        }}
                      >
                        PRESS
                      </button>
                    )}
                    <button
                      onClick={() => setDismissedTriggers(prev => new Set([...prev, triggerKey]))}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '9999px', cursor: 'pointer',
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        color: 'var(--accent-danger)',
                      }}
                    >
                      <X size={18} strokeWidth={3} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null
      })()}

      {/* Press confirmation modal */}
      {showPressConfirm && (
        <div
          onClick={() => setShowPressConfirm(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              Press this match?
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', textAlign: 'center', lineHeight: 1.5 }}>
              This will start a new press on the {showPressConfirm.segment === 'front' ? 'Front' : showPressConfirm.segment === 'back' ? 'Back' : 'Total'} with the {showPressConfirm.remaining} hole{showPressConfirm.remaining !== 1 ? 's' : ''} remaining. The original game continues alongside.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                className="btn-primary"
                onClick={() => {
                  handlePress(showPressConfirm)
                  setShowPressConfirm(null)
                }}
                style={{ height: '44px', width: '100%', fontSize: '0.9375rem', fontWeight: '600', borderRadius: '9999px' }}
              >
                Confirm Press
              </button>
              <button
                onClick={() => setShowPressConfirm(null)}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '9999px',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo press confirmation modal */}
      {pendingUndoPress && (
        <div
          onClick={() => setPendingUndoPress(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              border: '1px solid var(--border-color)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              Undo this press?
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', textAlign: 'center', lineHeight: 1.5 }}>
              This will remove the press and restore the original game.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                className="btn-primary"
                onClick={() => {
                  deletePressMutation.mutate({ pressId: pendingUndoPress.id, gameId: game.id })
                  setPendingUndoPress(null)
                }}
                style={{ height: '44px', width: '100%', fontSize: '0.9375rem', fontWeight: '600', borderRadius: '9999px' }}
              >
                Undo Press
              </button>
              <button
                onClick={() => setPendingUndoPress(null)}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '9999px',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'scorecard' ? (
        /* Full scorecard table — transposed: players as rows, holes as columns */
        <div style={{ overflowX: 'auto', marginBottom: '1.5rem', WebkitOverflowScrolling: 'touch', backgroundColor: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.25rem 0' }}>
          <table style={{
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            width: '100%',
          }}>
            <thead>
              {/* Hole number header row */}
              <tr>
                {hasTeams && (
                  <th style={{
                    padding: 0,
                    borderBottom: '1px solid var(--border-color)',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'var(--bg-secondary)',
                    zIndex: 3,
                    width: '22px',
                    minWidth: '22px',
                  }} />
                )}
                <th style={{
                  padding: '0.5rem 0.5rem',
                  textAlign: 'left',
                  color: 'var(--text-muted)',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  borderBottom: '1px solid var(--border-color)',
                  position: 'sticky',
                  left: hasTeams ? 22 : 0,
                  backgroundColor: 'var(--bg-secondary)',
                  zIndex: 2,
                  minWidth: '70px',
                }}>
                  Hole
                </th>
                {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => (
                  <React.Fragment key={hole}>
                    {numHoles === 18 && hole === 10 && (
                      <th style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', fontSize: '0.75rem', borderBottom: '1px solid var(--border-color)', borderLeft: '2px solid var(--border-color)', minWidth: '38px' }}>
                        OUT
                      </th>
                    )}
                    <th style={{
                      padding: '0.5rem 0.25rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--text-secondary)',
                      borderBottom: '1px solid var(--border-color)',
                      minWidth: '36px',
                      ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                    }}>
                      {hole}
                    </th>
                  </React.Fragment>
                ))}
                {numHoles === 18 && (
                  <th style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', fontSize: '0.75rem', borderBottom: '1px solid var(--border-color)', borderLeft: '2px solid var(--border-color)', minWidth: '38px' }}>
                    IN
                  </th>
                )}
                <th style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--accent-primary)', fontSize: '0.8125rem', borderBottom: '2px solid var(--accent-primary)', borderLeft: '2px solid var(--border-color)', minWidth: '40px' }}>
                  TOT
                </th>
                {skinsBet && (
                  <th style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--accent-primary)', fontSize: '0.75rem', borderBottom: '2px solid var(--accent-primary)', borderLeft: '2px solid var(--border-color)', minWidth: '40px' }}>
                    <span style={{ display: 'inline', verticalAlign: 'middle', fontWeight: '700', fontSize: '12px' }}>$</span>
                  </th>
                )}
                {ctpBet && (
                  <th style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--accent-secondary, #f59e0b)', fontSize: '0.75rem', borderBottom: '2px solid var(--accent-secondary, #f59e0b)', borderLeft: '2px solid var(--border-color)', minWidth: '40px' }}>
                    <Target size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Par row */}
              {course && (
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  {hasTeams && (
                    <td style={{
                      padding: 0,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'var(--bg-secondary)',
                      zIndex: 2,
                      width: '22px',
                      minWidth: '22px',
                    }} />
                  )}
                  <td style={{
                    padding: '0.5rem 0.5rem',
                    fontWeight: '600',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    position: 'sticky',
                    left: hasTeams ? 22 : 0,
                    backgroundColor: 'var(--bg-secondary)',
                    zIndex: 1,
                  }}>
                    Par
                  </td>
                  {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => (
                    <React.Fragment key={hole}>
                      {numHoles === 18 && hole === 10 && (
                        <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)', fontSize: '0.8125rem', borderLeft: '2px solid var(--border-color)' }}>
                          {course.par.slice(0, 9).reduce((s, p) => s + p, 0)}
                        </td>
                      )}
                      <td style={{
                        padding: '0.5rem 0.25rem',
                        textAlign: 'center',
                        color: 'var(--text-muted)',
                        fontSize: '0.8125rem',
                        ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                      }}>
                        {course.par[hole - 1]}
                      </td>
                    </React.Fragment>
                  ))}
                  {numHoles === 18 && (
                    <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)', fontSize: '0.8125rem', borderLeft: '2px solid var(--border-color)' }}>
                      {course.par.slice(9, 18).reduce((s, p) => s + p, 0)}
                    </td>
                  )}
                  <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>
                    {course.par.slice(0, numHoles).reduce((s, p) => s + p, 0)}
                  </td>
                  {skinsBet && (
                    <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                  {ctpBet && (
                    <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                </tr>
              )}

              {/* Skins row */}
              {skinsBet && skinsResult && (
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  {hasTeams && (
                    <td style={{
                      padding: 0,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'var(--bg-secondary)',
                      zIndex: 2,
                      width: '22px',
                      minWidth: '22px',
                    }} />
                  )}
                  <td style={{
                    padding: '0.375rem 0.5rem',
                    fontWeight: '600',
                    fontSize: '0.6875rem',
                    color: 'var(--accent-primary)',
                    position: 'sticky',
                    left: hasTeams ? 22 : 0,
                    backgroundColor: 'var(--bg-secondary)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    <span style={{ fontWeight: '700', fontSize: '11px' }}>$</span>
                    Skins
                  </td>
                  {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                    const holeSkin = skinsResult.details.find(d => d.hole === hole)
                    const gamePlayers = players.map(gp => gp.player?.id || gp.player_id).filter(Boolean)
                    const allScored = gamePlayers.every(pid => getScore(pid, hole) != null)
                    const won = allScored && holeSkin && holeSkin.skinsWon > 0
                    const winner = won ? players.find(gp => (gp.player?.id || gp.player_id) === holeSkin.winnerId) : null
                    return (
                      <React.Fragment key={hole}>
                        {numHoles === 18 && hole === 10 && (
                          <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                        )}
                        <td style={{
                          padding: '0.25rem 0.125rem',
                          textAlign: 'center',
                          fontSize: '0.625rem',
                          ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                        }}>
                          {won && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
                              <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '10px' }}>$</span>
                              <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.5625rem', lineHeight: 1.2 }}>
                                +{holeSkin.skinsWon}
                              </span>
                              <span style={{ color: winner?.player?.avatar_color || 'var(--text-muted)', fontWeight: '600', fontSize: '0.5rem', lineHeight: 1.1 }}>
                                {winner?.player?.display_name?.split(' ')[0]?.slice(0, 4) || ''}
                              </span>
                            </div>
                          )}
                        </td>
                      </React.Fragment>
                    )
                  })}
                  {numHoles === 18 && (
                    <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                  <td style={{
                    padding: '0.25rem',
                    textAlign: 'center',
                    borderLeft: '2px solid var(--border-color)',
                    fontWeight: '700',
                    fontSize: '0.6875rem',
                    color: 'var(--accent-primary)',
                  }}>
                    {skinsResult.totalSkins || ''}
                  </td>
                  <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  {ctpBet && (
                    <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                </tr>
              )}

              {/* CTP row */}
              {ctpBet && (
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  {hasTeams && (
                    <td style={{
                      padding: 0,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'var(--bg-secondary)',
                      zIndex: 2,
                      width: '22px',
                      minWidth: '22px',
                    }} />
                  )}
                  <td style={{
                    padding: '0.375rem 0.5rem',
                    fontWeight: '600',
                    fontSize: '0.6875rem',
                    color: 'var(--accent-secondary, #f59e0b)',
                    position: 'sticky',
                    left: hasTeams ? 22 : 0,
                    backgroundColor: 'var(--bg-secondary)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    <Target size={11} />
                    CTP
                  </td>
                  {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                    const isPar3 = par3Holes.includes(hole)
                    const ctpWinner = isPar3 ? (ctpResults || []).find((r) => r.hole_number === hole) : null
                    const winnerPlayer = ctpWinner ? players.find(gp => (gp.player?.id || gp.player_id) === ctpWinner.winner_player_id) : null
                    return (
                      <React.Fragment key={hole}>
                        {numHoles === 18 && hole === 10 && (
                          <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                        )}
                        <td style={{
                          padding: '0.25rem 0.125rem',
                          textAlign: 'center',
                          fontSize: '0.625rem',
                          ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                        }}>
                          {isPar3 && winnerPlayer && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
                              <Target size={10} style={{ color: 'var(--accent-secondary, #f59e0b)' }} />
                              <span style={{ color: winnerPlayer?.player?.avatar_color || 'var(--text-muted)', fontWeight: '600', fontSize: '0.5rem', lineHeight: 1.1 }}>
                                {winnerPlayer?.player?.display_name?.split(' ')[0]?.slice(0, 4) || ''}
                              </span>
                            </div>
                          )}
                          {isPar3 && !winnerPlayer && (
                            <Target size={10} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
                          )}
                        </td>
                      </React.Fragment>
                    )
                  })}
                  {numHoles === 18 && (
                    <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                  <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  {skinsBet && (
                    <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                  )}
                  <td style={{
                    padding: '0.25rem',
                    textAlign: 'center',
                    borderLeft: '2px solid var(--border-color)',
                    fontWeight: '700',
                    fontSize: '0.6875rem',
                    color: 'var(--accent-secondary, #f59e0b)',
                  }}>
                    {(ctpResults || []).filter((r) => r.winner_player_id).length || ''}
                  </td>
                </tr>
              )}

              {/* Player rows grouped by team */}
              {playersByTeam.map((teamGroup, teamIdx) => {
                const tColor = teamColors[teamIdx % teamColors.length]
                const matchRowCount = matchPlayHoleStatus ? 2 : 0
                const pressRowCount = pressHoleStatuses.length
                const strokePlayRowCount = strokePlayTeamScores ? 1 : 0
                const diffRowCount = teamIdx === 0 && strokePlayTeamScores?.diffs ? 2 : 0
                const extraRows = teamIdx === 0 ? matchRowCount + pressRowCount : 0
                const teamExtraRows = strokePlayRowCount + (teamIdx === 0 ? diffRowCount : 0)
                return (
                  <React.Fragment key={teamGroup.name || teamIdx}>
                    {(isScramble ? [teamGroup.players[0]] : teamGroup.players).map(({ player }, playerIdx) => {
                      if (!player) return null
                      const pid = player.id
                      const gross = getTotal(pid)
                      const netTot = getNetTotal(pid)

                      // Front/back 9 subtotals
                      const front9Scores = scores.filter((sc) => sc.player_id === player.id && sc.hole_number >= 1 && sc.hole_number <= 9 && sc.strokes != null)
                      const front9Gross = front9Scores.reduce((sum, sc) => sum + sc.strokes, 0)
                      const front9Net = handicapBreakdown
                        ? front9Scores.reduce((sum, sc) => sum + netScore(sc.strokes, getStrokesOnHole(player.id, sc.hole_number)), 0)
                        : null
                      const back9Scores = scores.filter((sc) => sc.player_id === player.id && sc.hole_number >= 10 && sc.hole_number <= 18 && sc.strokes != null)
                      const back9Gross = back9Scores.reduce((sum, sc) => sum + sc.strokes, 0)
                      const back9Net = handicapBreakdown
                        ? back9Scores.reduce((sum, sc) => sum + netScore(sc.strokes, getStrokesOnHole(player.id, sc.hole_number)), 0)
                        : null

                      const showNetFront = front9Net != null && front9Net !== front9Gross
                      const showNetBack = back9Net != null && back9Net !== back9Gross
                      const showNetTotal = netTot != null && netTot !== gross

                      return (
                        <tr key={player.id} style={{
                          borderBottom: '1px solid var(--border-color)',
                          ...(hasTeams && teamIdx > 0 && playerIdx === 0 ? { borderTop: '2px solid var(--border-color)' } : {}),
                        }}>
                          {/* Team label cell - vertical text on first player of each team */}
                          {hasTeams && playerIdx === 0 && (
                            <td
                              rowSpan={(isScramble ? 1 : teamGroup.players.length) + extraRows + teamExtraRows}
                              style={{
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'var(--bg-secondary)',
                                zIndex: 2,
                                width: '22px',
                                minWidth: '22px',
                                maxWidth: '22px',
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                                textAlign: 'center',
                                fontWeight: '800',
                                fontSize: '0.5625rem',
                                color: tColor,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                padding: '0.375rem 0.125rem',
                                boxShadow: `inset -2px 0 0 0 ${tColor}`,
                              }}
                            >
                              {teamGroup.name}
                            </td>
                          )}
                          <td style={{
                            padding: '0.5rem 0.5rem',
                            fontWeight: '600',
                            fontSize: '0.8125rem',
                            color: isScramble ? 'var(--text-primary)' : player.avatar_color,
                            position: 'sticky',
                            left: hasTeams ? 22 : 0,
                            backgroundColor: 'var(--bg-secondary)',
                            zIndex: 1,
                          }}>
                            {isScramble ? teamGroup.players.map(({ player: p }) => p?.display_name?.split(' ')[0]).filter(Boolean).join(' / ') : player.display_name.split(' ')[0]}
                            {(() => {
                              const hcp = getPlayerHandicap(player.id)
                              return hcp && hcp.adjustedStrokes > 0
                                ? ` (${hcp.adjustedStrokes})`
                                : null
                            })()}
                            {(() => {
                              const mt = getMixedTees(gameData)
                              const playerTee = mt?.[player.id]
                              const defaultTee = getGameTee(gameData)
                              return playerTee && playerTee.name !== defaultTee?.name ? (
                                <div style={{ fontSize: '0.5rem', fontWeight: '500', color: 'var(--text-muted)', lineHeight: 1, marginTop: '0.125rem' }}>
                                  {playerTee.name}
                                </div>
                              ) : null
                            })()}
                          </td>
                          {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                            const s = getScore(player.id, hole)
                            const strk = getStrokesOnHole(player.id, hole)
                            const net = s != null ? netScore(s, strk) : null
                            const hasStroke = strk > 0

                            return (
                              <React.Fragment key={hole}>
                                {/* OUT (front 9) - inline before hole 10 */}
                                {numHoles === 18 && hole === 10 && (
                                  <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }}>
                                    <div style={{ fontWeight: '700', fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                                      {front9Gross || '–'}
                                    </div>
                                    {showNetFront && front9Gross > 0 && (
                                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{front9Net}</div>
                                    )}
                                  </td>
                                )}
                                <td
                                  onClick={() => {
                                    if (canEditScores) {
                                      setCurrentHole(hole)
                                      setViewMode('entry')
                                      setTimeout(() => handleScoreTap(player.id, hole), 50)
                                    }
                                  }}
                                  style={{
                                    padding: '0.375rem 0.1875rem',
                                    textAlign: 'center',
                                    backgroundColor: hasStroke ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'transparent',
                                    cursor: canEditScores ? 'pointer' : 'default',
                                    ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                  }}
                                >
                                  <ScoreIndicator gross={s} net={net} par={course?.par[hole - 1]} />
                                  {isStableford && s != null && course?.par[hole - 1] != null && (
                                    <div style={{ fontSize: '0.5rem', fontWeight: '700', color: 'var(--accent-secondary, #f59e0b)', lineHeight: 1 }}>
                                      {stablefordPoints(net, course.par[hole - 1])}pt
                                    </div>
                                  )}
                                </td>
                              </React.Fragment>
                            )
                          })}
                          {/* IN (back 9) */}
                          {numHoles === 18 && (
                            <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }}>
                              <div style={{ fontWeight: '700', fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                                {back9Gross || '–'}
                              </div>
                              {showNetBack && back9Gross > 0 && (
                                <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{back9Net}</div>
                              )}
                            </td>
                          )}
                          {/* TOT */}
                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }}>
                            <div style={{ fontWeight: '800', fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                              {gross || '–'}
                            </div>
                            {showNetTotal && gross > 0 && (
                              <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{netTot}</div>
                            )}
                          </td>
                          {/* Skins count */}
                          {skinsBet && (
                            <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }}>
                              {skinsResult?.skinsByPlayer?.[player.id] > 0 && (
                                <div style={{ fontWeight: '700', fontSize: '0.8125rem', color: 'var(--accent-primary)' }}>
                                  {skinsResult.skinsByPlayer[player.id]}
                                </div>
                              )}
                            </td>
                          )}
                          {/* CTP count */}
                          {ctpBet && (
                            <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }}>
                              {(() => {
                                const ctpWins = (ctpResults || []).filter((r) => r.winner_player_id === player.id).length
                                return ctpWins > 0 ? (
                                  <div style={{ fontWeight: '700', fontSize: '0.8125rem', color: 'var(--accent-secondary, #f59e0b)' }}>
                                    {ctpWins}
                                  </div>
                                ) : null
                              })()}
                            </td>
                          )}
                        </tr>
                      )
                    })}

                    {/* Stroke play team total row (Stableford / Six Pack / High-Low) */}
                    {strokePlayTeamScores && (
                      <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                        <td style={{
                          padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: tColor,
                          position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                        }}>
                          Total
                        </td>
                        {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                          const val = strokePlayTeamScores.running[teamIdx]?.[hole]
                          const borderL = { borderLeft: '2px solid var(--border-color)' }
                          return (
                            <React.Fragment key={hole}>
                              {numHoles === 18 && hole === 10 && (
                                <td style={{
                                  padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                                  color: tColor, ...borderL,
                                }}>
                                  {strokePlayTeamScores.front[teamIdx] || '–'}
                                </td>
                              )}
                              <td style={{
                                padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '700',
                                color: val != null ? tColor : 'transparent',
                              }}>
                                {val ?? ''}
                              </td>
                            </React.Fragment>
                          )
                        })}
                        {numHoles === 18 && (
                          <td style={{
                            padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                            color: tColor, borderLeft: '2px solid var(--border-color)',
                          }}>
                            {strokePlayTeamScores.back[teamIdx] || '–'}
                          </td>
                        )}
                        <td style={{
                          padding: '0.25rem', textAlign: 'center', fontSize: '0.6875rem', fontWeight: '800',
                          color: tColor, borderLeft: '2px solid var(--border-color)',
                        }}>
                          {strokePlayTeamScores.overall[teamIdx] || '–'}
                        </td>
                        {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                      </tr>
                    )}

                    {/* Stroke play diff rows (FRONT/BACK + TOTAL) — after Team A only */}
                    {teamIdx === 0 && strokePlayTeamScores?.diffs && (
                      <>
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{
                            padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.5625rem', color: 'var(--text-muted)',
                            position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                          }}>
                            Front/Back
                          </td>
                          {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                            const diff = strokePlayTeamScores.diffs.frontBackDiff[hole]
                            const fmt = formatPointDiff(diff)
                            const borderL = { borderLeft: '2px solid var(--border-color)' }
                            return (
                              <React.Fragment key={hole}>
                                {numHoles === 18 && hole === 10 && (() => {
                                  const fDiff = (strokePlayTeamScores.front[0] - strokePlayTeamScores.front[1]) * strokePlayTeamScores.diffs.sign
                                  const fFmt = formatPointDiff(fDiff)
                                  return (
                                    <td style={{
                                      padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                      color: fFmt?.color || 'var(--text-muted)', ...borderL,
                                      ...(strokePlayTeamScores.frontWinner != null ? { boxShadow: `inset 0 0 0 2px ${fFmt?.color}` } : {}),
                                    }}>
                                      {fFmt?.text || '–'}
                                    </td>
                                  )
                                })()}
                                <td style={{
                                  padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                  color: fmt?.color || 'transparent',
                                }}>
                                  {fmt?.text || ''}
                                </td>
                              </React.Fragment>
                            )
                          })}
                          {numHoles === 18 && (() => {
                            const bDiff = (strokePlayTeamScores.back[0] - strokePlayTeamScores.back[1]) * strokePlayTeamScores.diffs.sign
                            const bFmt = formatPointDiff(bDiff)
                            return (
                              <td style={{
                                padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                color: bFmt?.color || 'var(--text-muted)', borderLeft: '2px solid var(--border-color)',
                                ...(strokePlayTeamScores.backWinner != null ? { boxShadow: `inset 0 0 0 2px ${bFmt?.color}` } : {}),
                              }}>
                                {bFmt?.text || '–'}
                              </td>
                            )
                          })()}
                          <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                          {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                          {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        </tr>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <td style={{
                            padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.5625rem', color: 'var(--text-muted)',
                            position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                          }}>
                            Total
                          </td>
                          {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                            const diff = strokePlayTeamScores.diffs.overallDiff[hole]
                            const fmt = formatPointDiff(diff)
                            return (
                              <React.Fragment key={hole}>
                                {numHoles === 18 && hole === 10 && <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />}
                                <td style={{
                                  padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                  color: fmt?.color || 'transparent',
                                }}>
                                  {fmt?.text || ''}
                                </td>
                              </React.Fragment>
                            )
                          })}
                          {numHoles === 18 && <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />}
                          {(() => {
                            const oDiff = (strokePlayTeamScores.overall[0] - strokePlayTeamScores.overall[1]) * strokePlayTeamScores.diffs.sign
                            const oFmt = formatPointDiff(oDiff)
                            return (
                              <td style={{
                                padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                                color: oFmt?.color || 'var(--text-muted)', borderLeft: '2px solid var(--border-color)',
                                ...(strokePlayTeamScores.overallWinner != null ? { boxShadow: `inset 0 0 0 2px ${oFmt?.color}` } : {}),
                              }}>
                                {oFmt?.text || '–'}
                              </td>
                            )
                          })()}
                          {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                          {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        </tr>
                      </>
                    )}

                    {/* Match play status rows — rendered after first team (teamIdx === 0) */}
                    {teamIdx === 0 && matchPlayHoleStatus && (
                      <>
                        {/* Row 1: Front/Back match play running status */}
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{
                            padding: '0.375rem 0.5rem',
                            fontWeight: '700',
                            fontSize: '0.625rem',
                            color: 'var(--text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            position: 'sticky',
                            left: hasTeams ? 22 : 0,
                            backgroundColor: 'var(--bg-secondary)',
                            zIndex: 1,
                            whiteSpace: 'nowrap',
                          }}>
                            Front/Back
                          </td>
                          {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                            const isFront = hole <= 9
                            const segStatus = isFront ? matchPlayHoleStatus.front : matchPlayHoleStatus.back
                            const clinch = isFront ? matchPlayHoleStatus.frontClinch : matchPlayHoleStatus.backClinch
                            const isClinchHole = clinch && hole === clinch.clinchHole
                            const isAfterClinch = clinch && hole > clinch.clinchHole
                            const clinchColor = clinch?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                            return (
                              <React.Fragment key={hole}>
                                {numHoles === 18 && hole === 10 && (() => {
                                  const fc = matchPlayHoleStatus.frontClinch
                                  if (fc) {
                                    const c = fc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                    return (
                                      <td style={{
                                        padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                        color: c, boxShadow: `inset 0 0 0 2px ${c}`,
                                        borderLeft: '2px solid var(--border-color)',
                                      }}>
                                        {fc.text}
                                      </td>
                                    )
                                  }
                                  const frontFinal = formatMatchDiff(matchPlayHoleStatus.front[9])
                                  return (
                                    <td style={{
                                      padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                      color: frontFinal?.color || 'var(--text-muted)',
                                      borderLeft: '2px solid var(--border-color)',
                                    }}>
                                      {frontFinal?.text || '–'}
                                    </td>
                                  )
                                })()}
                                {isAfterClinch ? (
                                  <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}) }} />
                                ) : isClinchHole ? (
                                  <td style={{
                                    padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                    color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                    ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                  }}>
                                    {clinch.text}
                                  </td>
                                ) : (() => {
                                  const fmt = formatMatchDiff(segStatus[hole])
                                  return (
                                    <td style={{
                                      padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                      color: fmt?.color || 'transparent',
                                      ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                    }}>
                                      {fmt?.text || ''}
                                    </td>
                                  )
                                })()}
                              </React.Fragment>
                            )
                          })}
                          {numHoles === 18 && (() => {
                            const bc = matchPlayHoleStatus.backClinch
                            if (bc) {
                              const c = bc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                              return (
                                <td style={{
                                  padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                  color: c, boxShadow: `inset 0 0 0 2px ${c}`,
                                  borderLeft: '2px solid var(--border-color)',
                                }}>
                                  {bc.text}
                                </td>
                              )
                            }
                            const backFinal = formatMatchDiff(matchPlayHoleStatus.back[18])
                            return (
                              <td style={{
                                padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                color: backFinal?.color || 'var(--text-muted)',
                                borderLeft: '2px solid var(--border-color)',
                              }}>
                                {backFinal?.text || '–'}
                              </td>
                            )
                          })()}
                          <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                          {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                          {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        </tr>

                        {/* Row 2: Overall match play running status */}
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{
                            padding: '0.375rem 0.5rem',
                            fontWeight: '700',
                            fontSize: '0.625rem',
                            color: 'var(--text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            position: 'sticky',
                            left: hasTeams ? 22 : 0,
                            backgroundColor: 'var(--bg-secondary)',
                            zIndex: 1,
                            whiteSpace: 'nowrap',
                          }}>
                            Total
                          </td>
                          {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                            const oc = matchPlayHoleStatus.overallClinch
                            const isClinchHole = oc && hole === oc.clinchHole
                            const isAfterClinch = oc && hole > oc.clinchHole
                            const clinchColor = oc?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                            return (
                              <React.Fragment key={hole}>
                                {numHoles === 18 && hole === 10 && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                                )}
                                {isAfterClinch ? (
                                  <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}) }} />
                                ) : isClinchHole ? (
                                  <td style={{
                                    padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                    color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                    ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                  }}>
                                    {oc.text}
                                  </td>
                                ) : (() => {
                                  const fmt = formatMatchDiff(matchPlayHoleStatus.overall[hole])
                                  return (
                                    <td style={{
                                      padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                      color: fmt?.color || 'transparent',
                                      ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                    }}>
                                      {fmt?.text || ''}
                                    </td>
                                  )
                                })()}
                              </React.Fragment>
                            )
                          })}
                          {numHoles === 18 && (
                            <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                          )}
                          {(() => {
                            const oc = matchPlayHoleStatus.overallClinch
                            if (oc) {
                              const c = oc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                              return (
                                <td style={{
                                  padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                                  color: c, boxShadow: `inset 0 0 0 2px ${c}`,
                                  borderLeft: '2px solid var(--border-color)',
                                }}>
                                  {oc.text}
                                </td>
                              )
                            }
                            const or2 = matchPlayHoleStatus.overallResult
                            if (or2?.winner != null) {
                              const c = or2.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                              const txt = teamAResultText(or2) || '–'
                              return (
                                <td style={{
                                  padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                                  color: c, boxShadow: `inset 0 0 0 2px ${c}`,
                                  borderLeft: '2px solid var(--border-color)',
                                }}>
                                  {txt}
                                </td>
                              )
                            }
                            const overallFinal = formatMatchDiff(matchPlayHoleStatus.overall[numHoles])
                            return (
                              <td style={{
                                padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                                color: overallFinal?.color || 'var(--text-muted)',
                                borderLeft: '2px solid var(--border-color)',
                              }}>
                                {overallFinal?.text || '–'}
                              </td>
                            )
                          })()}
                          {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                          {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        </tr>
                      </>
                    )}

                    {/* Press status rows — rendered after first team (teamIdx === 0) */}
                    {teamIdx === 0 && pressHoleStatuses.map(({ press, status, segment, startHole, clinch: pressClinch, segResult: pressResult, isRepress, segLabel: customLabel }, pressIdx) => {
                      const segEnd = segment === 'front' ? 9 : 18
                      const segLabel = customLabel || (segment === 'front' ? 'Press F' : segment === 'back' ? 'Press B' : 'Press T')
                      return (
                        <tr key={press.id || pressIdx} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'rgba(239, 68, 68, 0.03)' }}>
                          <td style={{
                            padding: '0.375rem 0.5rem',
                            fontWeight: '700',
                            fontSize: '0.5625rem',
                            color: 'var(--accent-danger, #ef4444)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            position: 'sticky',
                            left: hasTeams ? 22 : 0,
                            backgroundColor: 'var(--bg-secondary)',
                            zIndex: 1,
                            whiteSpace: 'nowrap',
                          }}>
                            {segLabel}
                          </td>
                          {(() => {
                            const pressSummaryCell = (() => {
                              const bL = { borderLeft: '2px solid var(--border-color)' }
                              if (pressClinch) {
                                const c = pressClinch.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...bL }}>{pressClinch.text}</td>
                              }
                              if (pressResult?.winner != null) {
                                const c = pressResult.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                const txt = teamAResultText(pressResult) || '–'
                                return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...bL }}>{txt}</td>
                              }
                              const pf = isHighLow ? formatPointDiff(status[segEnd]) : formatMatchDiff(status[segEnd])
                              return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: pf?.color || 'var(--text-muted)', ...bL }}>{pf?.text || '–'}</td>
                            })()
                            const isFrontPress = segment === 'front'
                            return Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                              const inRange = hole >= startHole && hole <= segEnd
                              const isClinchHole = pressClinch && hole === pressClinch.clinchHole
                              const isAfterClinch = pressClinch && hole > pressClinch.clinchHole && inRange
                              const clinchColor = pressClinch?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                              return (
                                <React.Fragment key={hole}>
                                  {numHoles === 18 && hole === 10 && (
                                    isFrontPress ? pressSummaryCell : <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                                  )}
                                  {isAfterClinch ? (
                                    <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}) }} />
                                  ) : isClinchHole ? (
                                    <td style={{
                                      padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                      color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                      ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                    }}>
                                      {pressClinch.text}
                                    </td>
                                  ) : (() => {
                                    const fmt = inRange ? (isHighLow ? formatPointDiff(status[hole]) : formatMatchDiff(status[hole])) : null
                                    return (
                                      <td style={{
                                        padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                        color: fmt?.color || 'transparent',
                                        ...(numHoles === 18 && hole === 10 ? { borderLeft: '2px solid var(--border-color)' } : {}),
                                      }}>
                                        {fmt?.text || ''}
                                      </td>
                                    )
                                  })()}
                                </React.Fragment>
                              )
                            })
                          })()}
                          {numHoles === 18 && (
                            segment === 'back'
                              ? (() => {
                                  const bL = { borderLeft: '2px solid var(--border-color)' }
                                  if (pressClinch) {
                                    const c = pressClinch.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                    return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...bL }}>{pressClinch.text}</td>
                                  }
                                  if (pressResult?.winner != null) {
                                    const c = pressResult.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                    const txt = teamAResultText(pressResult) || '–'
                                    return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...bL }}>{txt}</td>
                                  }
                                  const pf = isHighLow ? formatPointDiff(status[segEnd]) : formatMatchDiff(status[segEnd])
                                  return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: pf?.color || 'var(--text-muted)', ...bL }}>{pf?.text || '–'}</td>
                                })()
                              : <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                          )}
                          <td style={{ padding: '0.25rem', textAlign: 'center', borderLeft: '2px solid var(--border-color)' }} />
                          {skinsBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                          {ctpBet && <td style={{ padding: '0.25rem', borderLeft: '2px solid var(--border-color)' }} />}
                        </tr>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <>
      {/* Hole navigator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        marginBottom: '1.25rem',
        padding: '0.75rem',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
      }}>
        <button
          onClick={() => setCurrentHole((h) => Math.max(1, h - 1))}
          disabled={currentHole <= 1}
          style={{
            width: '56px', height: '56px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '9999px',
            backgroundColor: 'var(--bg-tertiary)',
            opacity: currentHole <= 1 ? 0.3 : 1,
          }}
        >
          <ChevronLeft size={28} />
        </button>
        <div style={{ textAlign: 'center', minWidth: '80px' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.125rem' }}>
            Hole
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: '900', lineHeight: 1 }}>
            {currentHole}
          </div>
          {holePar != null && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '600', marginTop: '0.25rem' }}>
              Par {holePar}{tee?.yardages ? ` \u00B7 ${tee.yardages[currentHole - 1]} yds` : ''}
            </div>
          )}
          {course && (
            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem', opacity: 0.7 }}>
              HCP {course.handicapIndex[currentHole - 1]}
            </div>
          )}
        </div>

        <button
          onClick={() => navigateNextWithCtpCheck((h) => Math.min(numHoles, h + 1))}
          disabled={currentHole >= numHoles}
          style={{
            width: '56px', height: '56px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '9999px',
            backgroundColor: 'var(--bg-tertiary)',
            opacity: currentHole >= numHoles ? 0.3 : 1,
          }}
        >
          <ChevronRight size={28} />
        </button>
      </div>

      {/* CTP indicator on par 3 holes */}
      {ctpBet && isCurrentHolePar3 && (
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'rgba(245, 158, 11, 0.18)',
          borderRadius: '0.75rem',
          border: '2px solid rgba(245, 158, 11, 0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Target size={18} style={{ color: '#f59e0b' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: '800', color: '#f59e0b' }}>
                CTP — ${Number(ctpBet.amount)}/pp
              </span>
            </div>
            {currentCtpWinner ? (
              <button
                onClick={() => canEditScores && setShowCtpPicker(currentHole)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.25rem 0.625rem',
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                  border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.3)',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: 'var(--accent-primary)',
                }}
              >
                <Trophy size={12} />
                {(() => {
                  const wp = players.find((gp) => gp.player_id === currentCtpWinner.winner_player_id)
                  return wp?.player?.display_name || 'Winner'
                })()}
              </button>
            ) : noWinnerCtpHoles.has(currentHole) ? (
              <button
                onClick={() => canEditScores && setShowCtpPicker(currentHole)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.25rem 0.625rem',
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(var(--accent-danger-rgb, 239, 68, 68), 0.1)',
                  border: '1px solid rgba(var(--accent-danger-rgb, 239, 68, 68), 0.2)',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                }}
              >
                <X size={12} />
                No Winner
              </button>
            ) : canEditScores ? (
              <button
                onClick={() => setShowCtpPicker(currentHole)}
                style={{
                  padding: '0.25rem 0.625rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-muted)',
                }}
              >
                Pick Winner
              </button>
            ) : (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>No winner yet</span>
            )}
          </div>

          {/* CTP player picker dropdown */}
          {showCtpPicker === currentHole && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-color)',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {players.map(({ player, player_id }) => {
                  const pid = player?.id || player_id
                  if (!player) return null
                  const isWinner = currentCtpWinner?.winner_player_id === pid
                  return (
                    <button
                      key={pid}
                      onClick={() => handleCtpSelect(pid)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem',
                        borderRadius: '9999px',
                        backgroundColor: isWinner ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)' : 'transparent',
                        border: isWinner ? '1px solid var(--accent-primary)' : '1px solid transparent',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: player.avatar_color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '0.625rem',
                        color: '#fff',
                        flexShrink: 0,
                      }}>
                        {getInitials(player)}
                      </div>
                      <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                        {player.display_name}
                      </span>
                      {isWinner && <Check size={16} style={{ color: 'var(--accent-primary)' }} />}
                    </button>
                  )
                })}
                <div style={{ marginTop: '0.25rem', paddingTop: '0.375rem', borderTop: '1px solid var(--border-color)' }}>
                <button
                  onClick={handleCtpNoWinner}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '9999px',
                    backgroundColor: noWinnerCtpHoles.has(currentHole) ? 'rgba(var(--accent-danger-rgb, 239, 68, 68), 0.1)' : 'transparent',
                    border: noWinnerCtpHoles.has(currentHole) ? '1px solid var(--accent-danger, #ef4444)' : '1px solid transparent',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <X size={14} style={{ color: 'var(--text-muted)' }} />
                  </div>
                  <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500', color: 'var(--text-muted)' }}>
                    No Winner
                  </span>
                  {noWinnerCtpHoles.has(currentHole) && <Check size={16} style={{ color: 'var(--accent-danger, #ef4444)' }} />}
                </button>
                </div>
                {currentCtpWinner && (
                  <button
                    onClick={handleCtpClear}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem',
                      padding: '0.375rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--accent-danger, #ef4444)',
                      marginTop: '0.25rem',
                      borderTop: '1px solid var(--border-color)',
                      paddingTop: '0.5rem',
                    }}
                  >
                    <X size={14} />
                    Clear Winner
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowCtpPicker(null)}
                style={{
                  width: '100%',
                  padding: '0.375rem',
                  marginTop: '0.375rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hole result indicator (match play only) — hidden while a score popover is open */}
      {isMatchPlay && currentHoleResult && !currentHoleResult.pending && !(editingScore?.hole === currentHole) && (
        <div style={{
          textAlign: 'center',
          padding: '0.375rem 0.75rem',
          marginBottom: '0.75rem',
          borderRadius: '0.5rem',
          border: `1px solid ${holeWinnerIdx != null ? 'var(--accent-primary)' : 'rgba(234, 179, 8, 0.4)'}`,
          fontSize: '0.8125rem',
          fontWeight: '700',
          color: holeWinnerIdx != null ? 'var(--accent-primary)' : '#EAB308',
          backgroundColor: holeWinnerIdx != null ? 'var(--bg-secondary)' : 'rgba(234, 179, 8, 0.1)',
          letterSpacing: '0.04em',
        }}>
          {currentHoleResult.teamPoints
            ? (() => {
                const tp = currentHoleResult.teamPoints
                const lw = currentHoleResult.lowWinner
                const hw = currentHoleResult.highWinner
                const name = (idx) => playersByTeam[idx]?.name || `Team ${idx + 1}`
                if (tp[0] === 2) return `${name(0)} Sweeps High & Low! — 2 Points!`
                if (tp[1] === 2) return `${name(1)} Sweeps High & Low! — 2 Points!`
                if (tp[0] === 1 && tp[1] === 1) {
                  return `Split — ${name(lw)} Low, ${name(hw)} High`
                }
                if (tp[0] === 1) return `${name(0)} Wins 1 Point (${lw === 0 ? 'Low' : 'High'})`
                if (tp[1] === 1) return `${name(1)} Wins 1 Point (${lw === 1 ? 'Low' : 'High'})`
                return 'Push — No Blood!'
              })()
            : holeWinnerIdx != null
              ? `${playersByTeam[holeWinnerIdx]?.name || `Team ${holeWinnerIdx + 1}`} Wins Hole!`
              : 'Push — No Blood!'}
        </div>
      )}

      {/* Skins indicator for current hole — hidden while a score popover is open */}
      {skinsBet && !(editingScore?.hole === currentHole) && (() => {
        const holeSkin = skinsResult?.details?.find(d => d.hole === currentHole)
        const gamePlayers = players.map(gp => gp.player?.id || gp.player_id).filter(Boolean)
        const allScored = gamePlayers.every(pid =>
          getScore(pid, currentHole) != null ||
          (editingScore?.playerId === pid && editingScore?.hole === currentHole)
        )
        if (!allScored || !holeSkin || holeSkin.skinsWon === 0) return null
        const winner = players.find(gp => (gp.player?.id || gp.player_id) === holeSkin.winnerId)
        const winnerName = winner?.player?.display_name?.split(' ')[0] || 'Winner'
        return (
          <div style={{
            textAlign: 'center',
            padding: '0.375rem 0.75rem',
            marginBottom: '0.75rem',
            backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.3)',
          }}>
            <span style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.375rem', color: 'var(--accent-primary)', fontWeight: '700', fontSize: '14px' }}>$</span>
            <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
              +{holeSkin.skinsWon} Skin{holeSkin.skinsWon !== 1 ? 's' : ''} — {winnerName}
            </span>
          </div>
        )
      })()}

      {/* Score entry for current hole — grouped by team */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {playersByTeam.map((group, groupIdx) => {
          const sixPackStats = isSixPack ? (() => {
            const nineStart = currentHole <= 9 ? 1 : 10
            const nineEnd = currentHole <= 9 ? 9 : numHoles
            const teamPlayerIds = group.players.map((gp) => gp.player?.id || gp.player_id).filter(Boolean)
            return getSixPackStats(teamPlayerIds, nineStart, nineEnd)
          })() : null

          return (
          <div key={groupIdx} style={holeWinnerIdx === groupIdx ? {
            borderLeft: '3px solid var(--accent-primary)',
            paddingLeft: '0.625rem',
            borderRadius: '0.25rem',
          } : {}}>
            {group.name && groupIdx > 0 && (
              <div style={{
                height: '1px',
                backgroundColor: 'var(--border-color)',
                margin: '0.5rem 0 0.75rem',
              }} />
            )}
            {group.name && (
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '800',
                color: holeWinnerIdx === groupIdx ? 'var(--accent-primary)' : 'var(--text-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '0.5rem',
                paddingLeft: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}>
                {group.name}
              </div>
            )}
            {sixPackStats && (
              <div style={{
                fontSize: '0.6875rem',
                fontWeight: '600',
                color: sixPackStats.taken === 12 ? 'var(--accent-primary)' : sixPackStats.taken > 12 ? 'var(--accent-danger, #ef4444)' : 'var(--text-muted)',
                marginBottom: '0.375rem',
                paddingLeft: '0.25rem',
                display: 'flex',
                gap: '0.5rem',
              }}>
                <span>{currentHole <= 9 ? 'Front 9' : 'Back 9'}: {sixPackStats.taken}/12 taken</span>
                <span>&middot;</span>
                <span>{sixPackStats.needToDrop > 0 ? `${sixPackStats.needToDrop} drop${sixPackStats.needToDrop !== 1 ? 's' : ''} left` : '0 drops left'}</span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {isScramble ? (() => {
                // Scramble: one score entry per team, using first player as representative
                const repPlayer = group.players[0]
                if (!repPlayer?.player) return null
                const pid = repPlayer.player?.id || repPlayer.player_id
                const score = getScore(pid, currentHole)
                const strokesOnHole = getStrokesOnHole(pid, currentHole)
                const hasStroke = strokesOnHole > 0
                const isPopoverOpen = editingScore?.playerId === pid && editingScore?.hole === currentHole
                const net = score != null ? netScore(score, strokesOnHole) : null

                return (
                  <div
                    key={`scramble-${groupIdx}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '0.5rem',
                      border: `1px solid ${hasStroke ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                      boxShadow: hasStroke ? '0 0 0 1px rgba(var(--accent-primary-rgb, 14, 164, 122), 0.2)' : 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                        {group.players.map(({ player: p }) => p && (
                          <div key={p.id} style={{
                            width: '24px', height: '24px', borderRadius: '50%',
                            backgroundColor: p.avatar_color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: '700', fontSize: '0.5625rem', color: '#fff', flexShrink: 0,
                          }}>
                            {getInitials(p)}
                          </div>
                        ))}
                        <span style={{ fontWeight: '500', fontSize: '0.9375rem' }}>
                          {group.players.map(({ player: p }) => p?.display_name?.split(' ')[0]).filter(Boolean).join(' & ')}
                        </span>
                        {(() => {
                          const hcp = getPlayerHandicap(pid)
                          return hcp && hcp.adjustedStrokes > 0
                            ? <span style={{ fontWeight: '500', fontSize: '0.9375rem' }}>({hcp.adjustedStrokes})</span>
                            : null
                        })()}
                      </div>
                      {hasStroke && (
                        <div style={{
                          fontSize: '0.625rem', fontWeight: '600',
                          color: 'var(--accent-primary)', marginTop: '0.25rem',
                        }}>
                          Team gets {strokesOnHole === 1 ? '1 stroke' : `${strokesOnHole} strokes`}
                        </div>
                      )}
                    </div>

                    <div
                      ref={isPopoverOpen ? scoreAnchorRef : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        cursor: canEditScores ? 'pointer' : 'default',
                        opacity: canEditScores ? 1 : 0.7,
                        position: 'relative',
                      }}
                      onClick={() => canEditScores && handleScoreTap(pid, currentHole)}
                    >
                      {(isPopoverOpen ? editingScore.initialValue : score) != null ? (
                        <ScoreIndicator gross={isPopoverOpen ? editingScore.initialValue : score} net={isPopoverOpen ? netScore(editingScore.initialValue, strokesOnHole) : net} par={holePar} size="lg" />
                      ) : (
                        <div style={{
                          width: '48px', height: '48px', borderRadius: '0.5rem',
                          backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)',
                          fontSize: '1.25rem', fontWeight: '800',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '1px solid var(--border-color)',
                        }}>
                          –
                        </div>
                      )}
                    </div>
                    {isPopoverOpen && (
                      <ScorePopover
                        score={editingScore.value}
                        initialValue={editingScore.initialValue}
                        par={holePar || 4}
                        anchorRef={scoreAnchorRef}
                        onStep={handleScoreStep}
                        onClear={handleScoreClear}
                        onUndo={() => {
                          if (editingScore.initialValue != null && editingScore.initialValue !== editingScore.value) {
                            setEditingScore({ ...editingScore, value: editingScore.initialValue })
                          }
                        }}
                        onConfirm={() => {
                          saveScoreAndSixPack(editingScore.playerId, editingScore.hole, editingScore.value)
                          setEditingScore(null)
                        }}
                        onDismiss={() => setEditingScore(null)}
                        isDeleting={deleteScoreMutation.isPending}
                      />
                    )}
                  </div>
                )
              })() : group.players.map(({ player, player_id }) => {
                const pid = player?.id || player_id
                if (!player) return null
                const score = getScore(pid, currentHole)
                const strokesOnHole = getStrokesOnHole(pid, currentHole)
                const hasStroke = strokesOnHole > 0
                const isPopoverOpen = editingScore?.playerId === pid && editingScore?.hole === currentHole
                const net = score != null ? netScore(score, strokesOnHole) : null

                return (
                  <div
                    key={pid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '0.5rem',
                      border: `1px solid ${hasStroke ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                      boxShadow: hasStroke ? '0 0 0 1px rgba(var(--accent-primary-rgb, 14, 164, 122), 0.2)' : 'none',
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: player.avatar_color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '0.8125rem',
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                      {getInitials(player)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: '500', fontSize: '0.9375rem' }}>
                        {player.display_name}
                        {(() => {
                          const hcp = getPlayerHandicap(pid)
                          return hcp && hcp.adjustedStrokes > 0
                            ? ` (${hcp.adjustedStrokes})`
                            : null
                        })()}
                      </span>
                      {(() => {
                        const mt = getMixedTees(gameData)
                        const playerTee = mt?.[pid]
                        const defaultTee = getGameTee(gameData)
                        return playerTee && playerTee.name !== defaultTee?.name ? (
                          <div style={{ fontSize: '0.5625rem', fontWeight: '600', color: 'var(--text-muted)', marginTop: '0.0625rem' }}>
                            {playerTee.name} tees
                          </div>
                        ) : null
                      })()}
                      {hasStroke && (
                        <div style={{
                          fontSize: '0.625rem',
                          fontWeight: '600',
                          color: 'var(--accent-primary)',
                          marginTop: '0.0625rem',
                        }}>
                          {strokesOnHole === 1 ? '1 stroke' : `${strokesOnHole} strokes`}
                        </div>
                      )}
                      {isStableford && score != null && holePar != null && (
                        <div style={{
                          fontSize: '0.625rem',
                          fontWeight: '700',
                          color: 'var(--accent-secondary, #f59e0b)',
                          marginTop: '0.0625rem',
                        }}>
                          {stablefordPoints(net, holePar)} pts
                        </div>
                      )}
                    </div>

                    <div
                      ref={isPopoverOpen ? scoreAnchorRef : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: canEditScores ? 'pointer' : 'default',
                        opacity: canEditScores ? 1 : 0.7,
                        position: 'relative',
                      }}
                      onClick={() => canEditScores && handleScoreTap(pid, currentHole)}
                    >
                      {(isPopoverOpen ? editingScore.initialValue : score) != null ? (
                        <ScoreIndicator gross={isPopoverOpen ? editingScore.initialValue : score} net={isPopoverOpen ? netScore(editingScore.initialValue, strokesOnHole) : net} par={holePar} size="lg" />
                      ) : (
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '0.5rem',
                          backgroundColor: 'var(--bg-tertiary)',
                          color: 'var(--text-muted)',
                          fontSize: '1.25rem',
                          fontWeight: '800',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid var(--border-color)',
                        }}>
                          –
                        </div>
                      )}
                    </div>
                    {isPopoverOpen && (
                      <ScorePopover
                        score={editingScore.value}
                        initialValue={editingScore.initialValue}
                        par={holePar || 4}
                        anchorRef={scoreAnchorRef}
                        onStep={handleScoreStep}
                        onClear={handleScoreClear}
                        onUndo={() => {
                          if (editingScore.initialValue != null && editingScore.initialValue !== editingScore.value) {
                            setEditingScore({ ...editingScore, value: editingScore.initialValue })
                          }
                        }}
                        onConfirm={() => {
                          saveScoreAndSixPack(editingScore.playerId, editingScore.hole, editingScore.value)
                          setEditingScore(null)
                        }}
                        onDismiss={() => setEditingScore(null)}
                        isDeleting={deleteScoreMutation.isPending}
                      />
                    )}
                    {isSixPack && score != null && (sixPackStats?.taken ?? 0) !== 12 && !((sixPackStats?.taken ?? 0) > 12 && getScoreTaken(pid, currentHole) === false) && !(sixPackStats?.needToDrop === 0 && getScoreTaken(pid, currentHole) !== false) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSixPackToggle(pid, currentHole) }}
                        disabled={!canEditScores || (getScoreTaken(pid, currentHole) !== false && sixPackStats?.needToDrop === 0)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.6875rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          backgroundColor: getScoreTaken(pid, currentHole) === false
                            ? 'rgba(14, 164, 122, 0.15)'
                            : 'rgba(239, 68, 68, 0.1)',
                          color: getScoreTaken(pid, currentHole) === false
                            ? 'var(--accent-primary)'
                            : 'var(--accent-danger, #ef4444)',
                          border: `1px solid ${getScoreTaken(pid, currentHole) === false ? 'var(--accent-primary)' : 'rgba(239, 68, 68, 0.3)'}`,
                          cursor: canEditScores ? 'pointer' : 'default',
                          opacity: canEditScores ? 1 : 0.6,
                          flexShrink: 0,
                        }}
                      >
                        {getScoreTaken(pid, currentHole) === false ? 'TAKE' : 'DROP'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          )
        })}
      </div>

      {/* Bottom hole navigation */}
      {gameData.status !== 'completed' && (
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          marginTop: '1.5rem',
          marginBottom: '0.5rem',
        }}>
          <button
            onClick={() => setCurrentHole((h) => Math.max(1, h - 1))}
            disabled={currentHole <= 1}
            style={{
              flex: 1,
              padding: '0.875rem',
              fontSize: '0.9375rem',
              fontWeight: '700',
              borderRadius: '9999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              color: currentHole <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-color)',
              opacity: currentHole <= 1 ? 0.4 : 1,
              cursor: currentHole <= 1 ? 'default' : 'pointer',
            }}
          >
            <ChevronLeft size={18} />
            Back
          </button>

          {currentHole >= numHoles && canFinishMatchup ? (
            <button
              onClick={() => {
                const playerIds = players.map(p => p.player_id)
                const allComplete = playerIds.every(pid =>
                  Array.from({ length: numHoles }, (_, i) => i + 1).every(h =>
                    scores.some(s => s.player_id === pid && s.hole_number === h && s.strokes != null)
                  )
                )
                setScoresComplete(allComplete)
                setShowEndConfirm(true)
              }}
              style={{
                flex: 1,
                padding: '0.875rem',
                fontSize: '0.9375rem',
                fontWeight: '700',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                color: '#fff',
                backgroundColor: 'var(--accent-primary)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <Trophy size={16} />
              Finish
            </button>
          ) : (
            <button
              onClick={() => navigateNextWithCtpCheck((h) => Math.min(numHoles, h + 1))}
              disabled={currentHole >= numHoles}
              style={{
                flex: 1,
                padding: '0.875rem',
                fontSize: '0.9375rem',
                fontWeight: '700',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                color: currentHole >= numHoles ? 'var(--text-muted)' : '#fff',
                backgroundColor: currentHole >= numHoles ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                border: currentHole >= numHoles ? '1px solid var(--border-color)' : 'none',
                opacity: currentHole >= numHoles ? 0.4 : 1,
                cursor: currentHole >= numHoles ? 'default' : 'pointer',
              }}
            >
              Next
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}

      </>
      )}
      {/* Presses section — shows all activated presses */}
      {(() => {
        const activePresses = (presses || []).filter(p => p.metadata?.pressing_team != null)
        if (activePresses.length === 0) return null

        // Number presses per segment
        const segCounts = {}
        const pressCards = activePresses.map(p => {
          const seg = p.metadata?.segment || 'overall'
          segCounts[seg] = (segCounts[seg] || 0) + 1
          const label = getPressLabel(seg, segCounts[seg])
          const pressingTeamName = teamStructure[p.metadata?.pressing_team]?.name || 'Team'
          return { press: p, label, pressingTeamName, seg }
        })

        return (
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{
              fontSize: '0.75rem',
              fontWeight: '700',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.5rem',
            }}>
              Presses
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {pressCards.map(({ press, label, pressingTeamName }) => (
                <div
                  key={press.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                    padding: '0.625rem 1rem',
                    backgroundColor: 'rgba(245, 158, 11, 0.08)',
                    borderRadius: '0.75rem',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        fontSize: '0.6875rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: 'var(--accent-warning)',
                        backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                      }}>
                        {label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
                      {pressingTeamName} has pressed starting on hole {press.hole_start}
                    </div>
                  </div>
                  {canPress && (
                    <button
                      onClick={() => setPendingUndoPress(press)}
                      disabled={deletePressMutation.isPending}
                      style={{
                        display: 'flex', alignItems: 'center', color: 'var(--text-muted)', padding: '0.375rem',
                        borderRadius: '9999px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                        opacity: deletePressMutation.isPending ? 0.5 : 1,
                      }}
                    >
                      <Undo2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })()}


    </div>
  )
}

